import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import { toast } from "react-hot-toast";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import AppleIcon from "@mui/icons-material/Apple";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import DesktopWindowsRoundedIcon from "@mui/icons-material/DesktopWindowsRounded";
import DevicesRoundedIcon from "@mui/icons-material/DevicesRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import HeadphonesRoundedIcon from "@mui/icons-material/HeadphonesRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";

import { useStyle } from "../../context/styleContext/styleContext";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import Seo from "../../components/Seo";

function getCardImageScale(product) {
  const configuredScale = Number(product?.cardImageScale);

  if (Number.isFinite(configuredScale) && configuredScale > 0) {
    const normalizedScale =
      configuredScale > 1
        ? configuredScale / 100
        : configuredScale;

    return Math.min(1, Math.max(0.5, normalizedScale));
  }

  return product?.productURI === "d1-delay" ? 0.92 : 0.84;
}

function getDeviceKey(device, index) {
  return (
    device?._id ||
    device?.deviceID ||
    device?.machineID ||
    device?.ipAddress ||
    `device-${index}`
  );
}

export default function MyGear() {
  const { style } = useStyle();

  const [gear, setGear] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [platformFilter, setPlatformFilter] =
    useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOption, setSortOption] =
    useState("nameAsc");

  const [selectedProduct, setSelectedProduct] =
    useState(null);
  const [selectedLicense, setSelectedLicense] =
    useState(null);
  const [isDevicesModalOpen, setDevicesModalOpen] =
    useState(false);
  const [removingDeviceId, setRemovingDeviceId] =
    useState(null);

  const panelBackground =
    style.color1 || "rgb(39, 55, 77)";
  const panelBorder =
    style.color2 || "rgb(82, 109, 130)";
  const mutedText =
    style.color3 || "rgb(157, 178, 191)";
  const primaryText =
    style.color4 || "rgb(221, 230, 237)";
  const accentGreen =
    style.accentGreen || "rgb(24, 146, 80)";
  const darkPanel =
    style.darkPanel || "rgba(9, 12, 18, 0.45)";
  const darkerPanel =
    style.darkerPanel || "rgba(6, 9, 14, 0.55)";

  const getMyGear = useCallback(async () => {
    const token = localStorage.getItem("token");

    try {
      setIsLoading(true);

      const { data } = await axios.post(
        "/api/get_my_gear",
        { token }
      );

      if (data?.error) {
        toast.error(data.error);
        return;
      }

      setGear(
        Array.isArray(data?.products)
          ? data.products
          : []
      );
      setLicenses(
        Array.isArray(data?.licenses)
          ? data.licenses
          : []
      );
    } catch (error) {
      console.error("Unable to load My Gear:", error);
      toast.error("Unable to load your gear right now.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
    getMyGear();
  }, [getMyGear]);

  const licensesBySku = useMemo(() => {
    const map = new Map();

    licenses.forEach((license) => {
      const sku = license?.sku || license?.SKU;

      if (sku) {
        map.set(String(sku), license);
      }
    });

    return map;
  }, [licenses]);

  const filteredGear = useMemo(() => {
    const normalizedSearch = searchTerm
      .trim()
      .toLowerCase();

    return [...gear]
      .filter((product) => {
        if (
          platformFilter === "Mac" &&
          !product?.macDownloadURI
        ) {
          return false;
        }

        if (
          platformFilter === "Windows" &&
          !product?.windowsDownloadURI
        ) {
          return false;
        }

        if (!normalizedSearch) {
          return true;
        }

        const searchableText = [
          product?.name,
          product?.shortDescription,
          product?.cardSummary,
          product?.productURI,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return searchableText.includes(normalizedSearch);
      })
      .sort((a, b) => {
        const aName = String(a?.name || "");
        const bName = String(b?.name || "");

        if (sortOption === "nameDesc") {
          return bName.localeCompare(aName);
        }

        return aName.localeCompare(bName);
      });
  }, [
    gear,
    platformFilter,
    searchTerm,
    sortOption,
  ]);

  const libraryStats = useMemo(() => {
    let owned = 0;
    let subscription = 0;
    let activeDevices = 0;

    gear.forEach((product) => {
      const license = licensesBySku.get(
        String(product?.SKU)
      );

      if (license?.type === "own") {
        owned += 1;
      }

      if (license?.type === "subscribe") {
        subscription += 1;
      }

      activeDevices += Array.isArray(
        license?.machinesInstalledOn
      )
        ? license.machinesInstalledOn.length
        : 0;
    });

    return {
      owned,
      subscription,
      activeDevices,
    };
  }, [gear, licensesBySku]);

  const openDevicesModal = (product, license) => {
    setSelectedProduct(product);
    setSelectedLicense(license);
    setDevicesModalOpen(true);
  };

  const closeDevicesModal = () => {
    if (removingDeviceId) {
      return;
    }

    setSelectedProduct(null);
    setSelectedLicense(null);
    setDevicesModalOpen(false);
  };

  const removeDevice = async (deviceId) => {
    const token = localStorage.getItem("token");

    if (!selectedLicense?._id || !deviceId) {
      toast.error("Unable to identify this device.");
      return;
    }

    try {
      setRemovingDeviceId(deviceId);

      const { data } = await axios.post(
        "/api/remove_device_from_license",
        {
          token,
          licenseID: selectedLicense._id,
          ipAddress: deviceId,
        }
      );

      if (!data?.success) {
        toast.error(
          data?.error || "Failed to remove device."
        );
        return;
      }

      setSelectedLicense(data.updatedLicense);
      setLicenses((currentLicenses) =>
        currentLicenses.map((license) =>
          license?._id === data.updatedLicense?._id
            ? data.updatedLicense
            : license
        )
      );

      toast.success("Device removed.");
    } catch (error) {
      console.error("Error removing device:", error);
      toast.error("Error removing device.");
    } finally {
      setRemovingDeviceId(null);
    }
  };

  const licenseChip = (license) => {
    if (license?.type === "own") {
      return (
        <Chip
          icon={<WorkspacePremiumRoundedIcon />}
          label="Owned"
          size="small"
          sx={{
            height: 25,
            color: primaryText,
            backgroundColor:
              "rgba(24,146,80,0.18)",
            border:
              "1px solid rgba(72,197,126,0.32)",
            fontWeight: 800,
            fontSize: "0.72rem",
            "& .MuiChip-icon": {
              color: "#72d49b",
              fontSize: 16,
            },
          }}
        />
      );
    }

    if (license?.type === "subscribe") {
      return (
        <Chip
          icon={<AutorenewRoundedIcon />}
          label="Subscribed"
          size="small"
          sx={{
            height: 25,
            color: primaryText,
            backgroundColor:
              "rgba(24,80,146,0.48)",
            border:
              "1px solid rgba(72,126,197,0.82)",
            fontWeight: 800,
            fontSize: "0.72rem",
            "& .MuiChip-icon": {
              color: mutedText,
              fontSize: 16,
            },
          }}
        />
      );
    }

    return null;
  };

  const selectSx = {
    minWidth: 0,
    color: primaryText,
    backgroundColor: "#0b111a",
    borderRadius: "9px",

    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255,255,255,0.11)",
    },

    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255,255,255,0.20)",
    },

    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: accentGreen,
      borderWidth: "1px",
    },

    "& .MuiSvgIcon-root": {
      color: mutedText,
    },
  };

  const selectMenuProps = {
    PaperProps: {
      sx: {
        mt: 0.5,
        color: primaryText,
        backgroundColor: "#0b111a",
        backgroundImage: "none",
        border:
          "1px solid rgba(255,255,255,0.12)",
        boxShadow:
          "0 16px 36px rgba(0,0,0,0.60)",

        "& .MuiMenuItem-root": {
          fontSize: "0.9rem",
        },

        "& .MuiMenuItem-root:hover": {
          backgroundColor:
            "rgba(255,255,255,0.07)",
        },

        "& .Mui-selected": {
          backgroundColor:
            "rgba(24,146,80,0.16) !important",
        },
      },
    },
  };

  const emptyLibrary = (
    <Paper
      elevation={0}
      sx={{
        overflow: "hidden",
        borderRadius: "18px",
        backgroundColor: panelBackground,
        border: `1px solid ${panelBorder}`,
        boxShadow:
          "0 18px 44px rgba(0,0,0,0.32)",
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 4 },
          py: { xs: 4, sm: 5.5 },
          textAlign: "center",
        }}
      >
        <Box
          sx={{
            width: 62,
            height: 62,
            mx: "auto",
            mb: 1.6,
            display: "grid",
            placeItems: "center",
            borderRadius: "16px",
            color: "#ffffff",
            backgroundColor: accentGreen,
            border:
              "1px solid rgba(255,255,255,0.14)",
            boxShadow:
              "0 12px 28px rgba(0,0,0,0.28)",
          }}
        >
          <Inventory2RoundedIcon fontSize="large" />
        </Box>

        <Typography
          component="h1"
          sx={{
            color: primaryText,
            fontSize: {
              xs: "1.7rem",
              sm: "2.1rem",
            },
            fontWeight: 900,
            lineHeight: 1.15,
            letterSpacing: "-0.03em",
          }}
        >
          Your gear library is empty
        </Typography>

        <Typography
          sx={{
            maxWidth: 610,
            mx: "auto",
            mt: 1,
            color: mutedText,
            fontSize: "0.95rem",
            lineHeight: 1.7,
          }}
        >
          Plugins you have access to will be available for download here. 
        </Typography>

        <Button
          component={RouterLink}
          to="/products"
          startIcon={<HeadphonesRoundedIcon />}
          sx={{
            mt: 2.4,
            minHeight: 46,
            px: 2.4,
            borderRadius: "9px",
            textTransform: "none",
            color: "#ffffff",
            fontWeight: 850,
            backgroundColor: accentGreen,
            boxShadow:
              "0 9px 22px rgba(0,0,0,0.24)",

            "&:hover": {
              color: "#ffffff",
              backgroundColor: accentGreen,
              filter: "brightness(1.08)",
            },
          }}
        >
          Browse Plugins
        </Button>
        
      </Box>
    </Paper>
  );

  return (
    <>
      <Seo
        title="My Gear | Parish Audio"
        description="View your Parish Audio plugins, downloads, licenses, and installation details."
        canonical="https://parishaudio.com/my-gear"
        robots="noindex,follow"
      />

      <Box
        component="main"
        sx={{
          width: "100%",
          minHeight: "calc(100vh - 130px)",
          overflowX: "hidden",
          backgroundColor: "#000",
          px: { xs: 1.25, sm: 1.75, md: 2.5 },
          py: { xs: 1.5, md: 2.5 },
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 1180,
            mx: "auto",
          }}
        >
          {isLoading ? (
            <Box
              sx={{
                minHeight: 360,
                display: "grid",
                placeItems: "center",
              }}
            >
              <LoadingSpinner />
            </Box>
          ) : gear.length === 0 ? (
            emptyLibrary
          ) : (
            <Stack spacing={2}>
              <Paper
                elevation={0}
                sx={{
                  overflow: "hidden",
                  borderRadius: "18px",
                  backgroundColor: panelBackground,
                  backgroundImage: "none",
                  border: `1px solid ${panelBorder}`,
                  boxShadow:
                    "0 18px 44px rgba(0,0,0,0.30), inset 0 1px 0 rgba(255,255,255,0.025)",
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    overflow: "hidden",
                    px: { xs: 1.75, sm: 2.5 },
                    py: { xs: 1.8, sm: 2.25 },
                    backgroundColor: "#111a26",
                    borderBottom:
                      "1px solid rgba(255,255,255,0.075)",
                  }}
                >
                  <Box
                    sx={{
                      position: "absolute",
                      width: 210,
                      height: 210,
                      top: -135,
                      left: -70,
                      borderRadius: "50%",
                      backgroundColor:
                        "rgba(24,146,80,0.12)",
                      filter: "blur(5px)",
                      pointerEvents: "none",
                    }}
                  />

                  <Stack
                    direction={{
                      xs: "column",
                      sm: "row",
                    }}
                    spacing={1.5}
                    justifyContent="space-between"
                    alignItems={{
                      xs: "flex-start",
                      sm: "center",
                    }}
                    sx={{
                      position: "relative",
                      zIndex: 1,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1.25}
                      alignItems="center"
                    >
                      <Box
                        sx={{
                          width: 46,
                          height: 46,
                          flexShrink: 0,
                          display: "grid",
                          placeItems: "center",
                          borderRadius: "12px",
                          color: "#ffffff",
                          backgroundColor: accentGreen,
                          border:
                            "1px solid rgba(255,255,255,0.13)",
                          boxShadow:
                            "0 10px 24px rgba(0,0,0,0.28)",
                        }}
                      >
                        <Inventory2RoundedIcon />
                      </Box>

                      <Box>
                        <Typography
                          component="h1"
                          sx={{
                            color: primaryText,
                            fontSize: {
                              xs: "1.6rem",
                              sm: "2rem",
                            },
                            fontWeight: 900,
                            lineHeight: 1.1,
                            letterSpacing: "-0.03em",
                          }}
                        >
                          My Gear
                        </Typography>

                        <Typography
                          sx={{
                            mt: 0.4,
                            color: mutedText,
                            fontSize: "0.85rem",
                            lineHeight: 1.45,
                          }}
                        >
                          Download your plugins and manage
                          authorized devices.
                        </Typography>
                      </Box>
                    </Stack>

                    <Stack
                      direction="row"
                      spacing={0.7}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      <Chip
                        label={`${gear.length} plugin${
                          gear.length === 1 ? "" : "s"
                        }`}
                        size="small"
                        sx={{
                          color: primaryText,
                          backgroundColor:
                            "rgba(255,255,255,0.05)",
                          border:
                            "1px solid rgba(255,255,255,0.08)",
                          fontWeight: 800,
                        }}
                      />

                      {libraryStats.owned > 0 && (
                        <Chip
                          label={`${libraryStats.owned} owned`}
                          size="small"
                          sx={{
                            color: primaryText,
                            backgroundColor:
                              "rgba(24,146,80,0.15)",
                            border:
                              "1px solid rgba(72,197,126,0.26)",
                            fontWeight: 800,
                          }}
                        />
                      )}

                      {libraryStats.subscription > 0 && (
                        <Chip
                          label={`${libraryStats.subscription} subscribed`}
                          size="small"
                          sx={{
                            color: primaryText,
                            backgroundColor:
                                "rgba(24,80,146,0.48)",
                            border:
                              "1px solid rgba(72,126,197,0.82)",  
                            fontWeight: 800,
                          }}
                        />
                      )}
                    </Stack>
                  </Stack>
                </Box>

                <Box
                  sx={{
                    px: { xs: 1.5, sm: 2 },
                    py: { xs: 1.35, sm: 1.6 },
                  }}
                >
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "minmax(0,1fr) 160px 160px",
                      },
                      gap: 1,
                      alignItems: "center",
                    }}
                  >
                    <TextField
                      size="small"
                      value={searchTerm}
                      onChange={(event) =>
                        setSearchTerm(
                          event.target.value
                        )
                      }
                      placeholder="Search..."
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchRoundedIcon
                              sx={{
                                color: mutedText,
                                fontSize: 20,
                              }}
                            />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        minWidth: 0,

                        "& .MuiOutlinedInput-root": {
                          minHeight: 42,
                          color: primaryText,
                          backgroundColor: "#0b111a",
                          borderRadius: "9px",

                          "& fieldset": {
                            borderColor:
                              "rgba(255,255,255,0.11)",
                          },

                          "&:hover fieldset": {
                            borderColor:
                              "rgba(255,255,255,0.20)",
                          },

                          "&.Mui-focused fieldset": {
                            borderColor: accentGreen,
                            borderWidth: "1px",
                          },
                        },

                        "& input::placeholder": {
                          color: mutedText,
                          opacity: 1,
                        },
                      }}
                    />



                    <FormControl
                      size="small"
                    >
                      <InputLabel
                        sx={{
                          color: mutedText,
                          "&.Mui-focused": {
                            color: primaryText,
                          },
                        }}
                      >
                        Sort
                      </InputLabel>

                      <Select
                        value={sortOption}
                        label="Sort"
                        onChange={(event) =>
                          setSortOption(
                            event.target.value
                          )
                        }
                        MenuProps={selectMenuProps}
                        sx={selectSx}
                      >
                        <MenuItem value="nameAsc">
                          Name A–Z
                        </MenuItem>
                        <MenuItem value="nameDesc">
                          Name Z–A
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              </Paper>

              {filteredGear.length === 0 ? (
                <Paper
                  elevation={0}
                  sx={{
                    px: 2,
                    py: 4,
                    textAlign: "center",
                    borderRadius: "16px",
                    backgroundColor: panelBackground,
                    border: `1px solid ${panelBorder}`,
                  }}
                >
                  <SearchRoundedIcon
                    sx={{
                      color: mutedText,
                      fontSize: 38,
                      mb: 0.8,
                    }}
                  />

                  <Typography
                    sx={{
                      color: primaryText,
                      fontSize: "1.15rem",
                      fontWeight: 850,
                    }}
                  >
                    No matching plugins
                  </Typography>

                  <Typography
                    sx={{
                      mt: 0.4,
                      color: mutedText,
                      fontSize: "0.9rem",
                    }}
                  >
                    Try a different search or platform filter.
                  </Typography>
                </Paper>
              ) : (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, minmax(0, 1fr))",
                      md: "repeat(3, minmax(0, 1fr))",
                    },
                    gap: { xs: 1.25, sm: 1.5 },
                  }}
                >
                  {filteredGear.map((product) => {
                    const license =
                      licensesBySku.get(
                        String(product?.SKU)
                      );

                    const machines = Array.isArray(
                      license?.machinesInstalledOn
                    )
                      ? license.machinesInstalledOn
                      : [];

                    const image =
                      product?.images?.find(
                        (item) => item?.URI
                      ) ||
                      product?.images?.[0];

                    const imageScale =
                      getCardImageScale(product);

                    const hasWindows = Boolean(
                      product?.windowsDownloadURI
                    );
                    const hasMac = Boolean(
                      product?.macDownloadURI
                    );
                    const downloadCount =
                      Number(hasWindows) +
                      Number(hasMac);

                    const summary =
                      product?.shortDescription ||
                      product?.cardSummary ||
                      "Parish Audio plugin download.";

                    return (
                      <Paper
                        key={
                          product?.SKU ||
                          product?.productURI
                        }
                        elevation={0}
                        sx={{
                          minWidth: 0,
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          overflow: "hidden",
                          borderRadius: "15px",
                          backgroundColor:
                            panelBackground,
                          border: `1px solid ${panelBorder}`,
                          boxShadow:
                            "0 8px 22px rgba(0,0,0,0.24), inset 0 1px 0 rgba(255,255,255,0.025)",
                          transition:
                            "transform 180ms ease, border-color 180ms ease, box-shadow 180ms ease",

                          "&:hover": {
                            transform:
                              "translateY(-3px)",
                            borderColor:
                              "rgba(125,165,215,0.42)",
                            boxShadow:
                              "0 14px 30px rgba(0,0,0,0.32), inset 0 1px 0 rgba(255,255,255,0.04)",
                          },

                          "&:hover .my-gear-image": {
                            transform:
                              "scale(1.025)",
                          },
                        }}
                      >
                        <Box
                          component={RouterLink}
                          to={`/products/${product.productURI}`}
                          sx={{
                            height: 184,
                            flexShrink: 0,
                            m: 1.1,
                            mb: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                            borderRadius: "11px",
                            textDecoration: "none",
                            backgroundColor: "#080d14",
                            border:
                              "1px solid rgba(255,255,255,0.10)",
                            boxSizing: "border-box",
                            p: 0.65,
                          }}
                        >
                          <Box
                            className="my-gear-image"
                            component="img"
                            src={
                              image?.URI ||
                              "/default-image.png"
                            }
                            alt={
                              image?.altText ||
                              product?.name ||
                              "Product image"
                            }
                            sx={{
                              width: `${imageScale * 100}%`,
                              maxWidth: "100%",
                              maxHeight: 164,
                              objectFit: "contain",
                              display: "block",
                              transition:
                                "transform 180ms ease",
                            }}
                          />
                        </Box>

                        <Box
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            display: "flex",
                            flexDirection: "column",
                            p: 1.35,
                          }}
                        >
                          <Box sx={{ flex: 1 }}>
                            <Typography
                              component={RouterLink}
                              to={`/products/${product.productURI}`}
                              sx={{
                                display: "-webkit-box",
                                overflow: "hidden",
                                WebkitBoxOrient:
                                  "vertical",
                                WebkitLineClamp: 2,
                                color: primaryText,
                                fontSize: "1.08rem",
                                fontWeight: 900,
                                lineHeight: 1.2,
                                letterSpacing:
                                  "-0.015em",
                                textDecoration: "none",

                                "&:hover": {
                                  color: "#ffffff",
                                },
                              }}
                            >
                              {product.name}
                            </Typography>

                            <Typography
                              sx={{
                                mt: 0.65,
                                minHeight: "2.9em",
                                display: "-webkit-box",
                                overflow: "hidden",
                                WebkitBoxOrient:
                                  "vertical",
                                WebkitLineClamp: 2,
                                color: mutedText,
                                fontSize: "0.82rem",
                                lineHeight: 1.45,
                              }}
                            >
                              {summary}
                            </Typography>

                            <Stack
                              direction="row"
                              spacing={0.6}
                              flexWrap="wrap"
                              useFlexGap
                              sx={{ mt: 1 }}
                            >
                              {licenseChip(license)}

                              <Chip
                                icon={
                                  <DevicesRoundedIcon />
                                }
                                label={`${machines.length} device${
                                  machines.length === 1
                                    ? ""
                                    : "s"
                                }`}
                                size="small"
                                sx={{
                                  height: 25,
                                  color: primaryText,
                                  backgroundColor:
                                    "rgba(255,255,255,0.045)",
                                  border:
                                    "1px solid rgba(255,255,255,0.075)",
                                  fontWeight: 750,
                                  fontSize: "0.72rem",

                                  "& .MuiChip-icon": {
                                    color: mutedText,
                                    fontSize: 16,
                                  },
                                }}
                              />

                              {product?.productVersion && (
                                <Chip
                                  label={`v${product.productVersion}`}
                                  size="small"
                                  sx={{
                                    height: 25,
                                    color: mutedText,
                                    backgroundColor:
                                      "rgba(255,255,255,0.025)",
                                    border:
                                      "1px solid rgba(255,255,255,0.055)",
                                    fontWeight: 700,
                                    fontSize:
                                      "0.69rem",
                                  }}
                                />
                              )}
                            </Stack>
                          </Box>

                          <Divider
                            sx={{
                              my: 1.25,
                              borderColor:
                                "rgba(255,255,255,0.075)",
                            }}
                          />

                          <Stack spacing={0.8}>
                            <Stack
                              direction="row"
                              spacing={0.6}
                              alignItems="center"
                            >
                              <DownloadRoundedIcon
                                sx={{
                                  color: mutedText,
                                  fontSize: 17,
                                }}
                              />

                              <Typography
                                sx={{
                                  color: primaryText,
                                  fontSize: "0.8rem",
                                  fontWeight: 800,
                                }}
                              >
                                Download installer
                              </Typography>
                            </Stack>

                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns:
                                  downloadCount > 1
                                    ? "1fr 1fr"
                                    : "1fr",
                                gap: 0.65,
                              }}
                            >
                              {hasWindows && (
                                <Button
                                  href={
                                    product.windowsDownloadURI
                                  }
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  startIcon={
                                    <DesktopWindowsRoundedIcon />
                                  }
                                  endIcon={
                                    <OpenInNewRoundedIcon
                                      sx={{
                                        fontSize:
                                          "15px !important",
                                      }}
                                    />
                                  }
                                  sx={{
                                    minWidth: 0,
                                    minHeight: 42,
                                    px: 1,
                                    borderRadius: "8px",
                                    textTransform: "none",
                                    color: primaryText,
                                    fontSize: "0.78rem",
                                    fontWeight: 850,
                                    backgroundColor:
                                      accentGreen,
                                    boxShadow:
                                      "0 7px 16px rgba(0,0,0,0.20)",

                                    "&:hover": {
                                      color: "#ffffff",
                                      backgroundColor:
                                        accentGreen,
                                      filter:
                                        "brightness(1.08)",
                                    },
                                  }}
                                >
                                  Windows
                                </Button>
                              )}

                              {hasMac && (
                                <Button
                                  href={
                                    product.macDownloadURI
                                  }
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  startIcon={<AppleIcon />}
                                  endIcon={
                                    <OpenInNewRoundedIcon
                                      sx={{
                                        fontSize:
                                          "15px !important",
                                      }}
                                    />
                                  }
                                  sx={{
                                    minWidth: 0,
                                    minHeight: 42,
                                    px: 1,
                                    borderRadius: "8px",
                                    textTransform: "none",
                                    color: primaryText,
                                    fontSize: "0.78rem",
                                    fontWeight: 850,
                                    backgroundColor:
                                      accentGreen,
                                    boxShadow:
                                      "0 7px 16px rgba(0,0,0,0.20)",

                                    "&:hover": {
                                      color: "#ffffff",
                                      backgroundColor:
                                        accentGreen,
                                      filter:
                                        "brightness(1.08)",
                                    },
                                  }}
                                >
                                  macOS
                                </Button>
                              )}
                            </Box>

                            <Button
                              type="button"
                              onClick={() =>
                                openDevicesModal(
                                  product,
                                  license
                                )
                              }
                              disabled={
                                !license ||
                                machines.length === 0
                              }
                              startIcon={
                                <DevicesRoundedIcon />
                              }
                              sx={{
                                minHeight: 35,
                                alignSelf: "flex-start",
                                px: 0.25,
                                textTransform: "none",
                                color: mutedText,
                                fontSize: "0.78rem",
                                fontWeight: 800,

                                "&:hover": {
                                  color: primaryText,
                                  backgroundColor:
                                    "transparent",
                                },

                                "&.Mui-disabled": {
                                  color:
                                    "rgba(255,255,255,0.27)",
                                },
                              }}
                            >
                              {machines.length > 0
                                ? "Manage Devices"
                                : "No Devices Yet"}
                            </Button>
                          </Stack>
                        </Box>
                      </Paper>
                    );
                  })}
                </Box>
              )}
            </Stack>
          )}
        </Box>
      </Box>

      <Dialog
        open={isDevicesModalOpen}
        onClose={closeDevicesModal}
        fullWidth
        maxWidth="sm"
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0,0,0,0.76)",
            backdropFilter: "blur(5px)",
          },
        }}
        PaperProps={{
          sx: {
            width: "calc(100% - 24px)",
            maxHeight: "calc(100dvh - 24px)",
            overflow: "hidden",
            borderRadius: "16px",
            color: primaryText,
            backgroundColor: panelBackground,
            backgroundImage: "none",
            border: `1px solid ${panelBorder}`,
            boxShadow:
              "0 28px 70px rgba(0,0,0,0.56)",
          },
        }}
      >
        <DialogTitle
          sx={{
            position: "relative",
            px: { xs: 1.6, sm: 2.2 },
            py: 1.5,
            backgroundColor: "#111a26",
            borderBottom:
              "1px solid rgba(255,255,255,0.075)",
          }}
        >
          <Stack
            direction="row"
            spacing={1.1}
            alignItems="center"
            sx={{ pr: 4.5 }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                flexShrink: 0,
                display: "grid",
                placeItems: "center",
                borderRadius: "11px",
                color: "#ffffff",
                backgroundColor: accentGreen,
                border:
                  "1px solid rgba(255,255,255,0.13)",
              }}
            >
              <DevicesRoundedIcon fontSize="small" />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  color: primaryText,
                  fontSize: "1.15rem",
                  fontWeight: 900,
                  lineHeight: 1.2,
                }}
              >
                {selectedProduct?.name ||
                  selectedLicense?.productName ||
                  "Manage Devices"}
              </Typography>

              <Typography
                sx={{
                  mt: 0.2,
                  color: mutedText,
                  fontSize: "0.78rem",
                  lineHeight: 1.35,
                }}
              >
                Remove devices that should no longer be
                authorized.
              </Typography>
            </Box>
          </Stack>

          <IconButton
            type="button"
            aria-label="Close device manager"
            onClick={closeDevicesModal}
            disabled={Boolean(removingDeviceId)}
            sx={{
              position: "absolute",
              top: 14,
              right: 14,
              width: 34,
              height: 34,
              color: mutedText,
              backgroundColor: "rgba(0,0,0,0.18)",
              border:
                "1px solid rgba(255,255,255,0.07)",

              "&:hover": {
                color: primaryText,
                backgroundColor:
                  "rgba(0,0,0,0.32)",
              },
            }}
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            px: { xs: 1.4, sm: 2 },
            py: { xs: 1.4, sm: 1.8 },
            backgroundColor: panelBackground,
            overflowY: "auto",
          }}
        >
          {!selectedLicense?.machinesInstalledOn?.length ? (
            <Paper
              elevation={0}
              sx={{
                px: 2,
                py: 3,
                textAlign: "center",
                borderRadius: "12px",
                backgroundColor: darkPanel,
                border:
                  "1px solid rgba(255,255,255,0.07)",
              }}
            >
              <DevicesRoundedIcon
                sx={{
                  color: mutedText,
                  fontSize: 36,
                  mb: 0.5,
                }}
              />

              <Typography
                sx={{
                  color: primaryText,
                  fontWeight: 850,
                }}
              >
                No active devices
              </Typography>

              <Typography
                sx={{
                  mt: 0.35,
                  color: mutedText,
                  fontSize: "0.84rem",
                }}
              >
                This plugin has not been authorized on a
                device yet.
              </Typography>
            </Paper>
          ) : (
            <Stack spacing={1}>
              {selectedLicense.machinesInstalledOn.map(
                (device, index) => {
                  const deviceId =
                    device?.ipAddress ||
                    device?.machineID ||
                    device?.deviceID;

                  const key = getDeviceKey(
                    device,
                    index
                  );

                  const isRemoving =
                    removingDeviceId === deviceId;

                  return (
                    <Paper
                      key={key}
                      elevation={0}
                      sx={{
                        p: 1.35,
                        borderRadius: "11px",
                        backgroundColor: darkerPanel,
                        border:
                          "1px solid rgba(255,255,255,0.07)",
                      }}
                    >
                      <Stack
                        direction={{
                          xs: "column",
                          sm: "row",
                        }}
                        spacing={1.25}
                        justifyContent="space-between"
                        alignItems={{
                          xs: "stretch",
                          sm: "center",
                        }}
                      >
                        <Stack
                          direction="row"
                          spacing={1.1}
                          alignItems="center"
                          sx={{ minWidth: 0 }}
                        >
                          <Box
                            sx={{
                              width: 38,
                              height: 38,
                              flexShrink: 0,
                              display: "grid",
                              placeItems: "center",
                              borderRadius: "10px",
                              color: mutedText,
                              backgroundColor:
                                "rgba(255,255,255,0.045)",
                              border:
                                "1px solid rgba(255,255,255,0.065)",
                            }}
                          >
                            <DevicesRoundedIcon fontSize="small" />
                          </Box>

                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              sx={{
                                overflow: "hidden",
                                textOverflow:
                                  "ellipsis",
                                whiteSpace: "nowrap",
                                color: primaryText,
                                fontSize: "0.92rem",
                                fontWeight: 850,
                              }}
                            >
                              {device?.deviceName ||
                                "Unknown Device"}
                            </Typography>

                            <Typography
                              sx={{
                                mt: 0.15,
                                color: mutedText,
                                fontSize: "0.76rem",
                                lineHeight: 1.4,
                              }}
                            >
                              {device?.osName ||
                                "Unknown OS"}
                              {device?.ipAddress
                                ? ` · ${device.ipAddress}`
                                : ""}
                            </Typography>
                          </Box>
                        </Stack>

                        <Button
                          type="button"
                          onClick={() =>
                            removeDevice(deviceId)
                          }
                          disabled={
                            !deviceId ||
                            Boolean(removingDeviceId)
                          }
                          startIcon={
                            isRemoving ? (
                              <CircularProgress
                                size={15}
                                thickness={5}
                                sx={{
                                  color: "#ffb0b4",
                                }}
                              />
                            ) : (
                              <DeleteOutlineRoundedIcon />
                            )
                          }
                          sx={{
                            minHeight: 38,
                            px: 1.35,
                            borderRadius: "8px",
                            textTransform: "none",
                            color: "#ffb0b4",
                            fontSize: "0.78rem",
                            fontWeight: 850,
                            backgroundColor:
                              "rgba(190,45,55,0.10)",
                            border:
                              "1px solid rgba(255,112,120,0.22)",

                            "&:hover": {
                              color: "#ffd0d3",
                              backgroundColor:
                                "rgba(190,45,55,0.17)",
                              borderColor:
                                "rgba(255,112,120,0.34)",
                            },
                          }}
                        >
                          {isRemoving
                            ? "Removing..."
                            : "Remove Device"}
                        </Button>
                      </Stack>
                    </Paper>
                  );
                }
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 1.4, sm: 2 },
            py: 1.25,
            backgroundColor: "#111a26",
            borderTop:
              "1px solid rgba(255,255,255,0.075)",
          }}
        >
          <Button
            type="button"
            onClick={closeDevicesModal}
            disabled={Boolean(removingDeviceId)}
            sx={{
              minHeight: 38,
              px: 1.8,
              borderRadius: "8px",
              textTransform: "none",
              color: primaryText,
              fontWeight: 850,
              backgroundColor:
                "rgba(82,109,130,0.28)",
              border:
                "1px solid rgba(255,255,255,0.08)",

              "&:hover": {
                color: "#ffffff",
                backgroundColor:
                  "rgba(82,109,130,0.42)",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}


// import React, { useState, useEffect, useMemo } from 'react';
// import {
//   Box,
//   Grid,
//   Card,
//   CardContent,
//   Typography,
//   Button,
//   Select,
//   MenuItem,
//   FormControl,
//   InputLabel,
//   TextField,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   Divider,
//   IconButton,
//   Stack,
//   Paper,
//   Chip,
//   InputAdornment,
// } from '@mui/material';
// import CloseIcon from '@mui/icons-material/Close';
// import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
// import DevicesRoundedIcon from '@mui/icons-material/DevicesRounded';
// import DesktopWindowsRoundedIcon from '@mui/icons-material/DesktopWindowsRounded';
// import AppleIcon from '@mui/icons-material/Apple';
// import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';
// import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded';
// import { useStyle } from '../../context/styleContext/styleContext';
// import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
// import axios from 'axios';
// import { toast } from 'react-hot-toast';
// import Seo from '../../components/Seo';
// import { Link as RouterLink } from 'react-router-dom';

// export default function MyGear() {
//   const { style } = useStyle();
//   const [gear, setGear] = useState([]);
//   const [licenses, setLicenses] = useState([]);
//   const [isLoading, setIsLoading] = useState(true);
//   const [filter, setFilter] = useState('All');
//   const [searchTerm, setSearchTerm] = useState('');
//   const [sortOption, setSortOption] = useState('nameAsc');
//   const [selectedLicense, setSelectedLicense] = useState(null);
//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [height, setHeight] = useState(window.innerHeight);

//   useEffect(() => {
//     function handleResize() {
//       setHeight(window.innerHeight);
//     }

//     window.addEventListener('resize', handleResize);
//     getMyGear();

//     return () => {
//       window.removeEventListener('resize', handleResize);
//     };
//   }, []);

//   const getMyGear = async () => {
//     const token = localStorage.getItem('token');

//     try {
//       const { data } = await axios.post('/api/get_my_gear', { token });

//       if (data.error) {
//         toast.error(data.error);
//       } else {
//         setGear(data.products || []);
//         setLicenses(data.licenses || []);
//       }
//     } catch (error) {
//       console.log(error);
//       toast.error('Unable to load your gear right now.');
//     }

//     setIsLoading(false);
//   };

//   const getLicenseBySKU = (sku) =>
//     licenses.find((l) => l.sku === sku || l.SKU === sku);

//   const handleOpenDevicesModal = (license) => {
//     setSelectedLicense(license);
//     setIsModalOpen(true);
//   };

//   const handleCloseDevicesModal = () => {
//     setSelectedLicense(null);
//     setIsModalOpen(false);
//   };

//   const handleRemoveDevice = async (deviceId) => {
//     const token = localStorage.getItem('token');

//     try {
//       const { data } = await axios.post('/api/remove_device_from_license', {
//         token,
//         licenseID: selectedLicense._id,
//         ipAddress: deviceId,
//       });

//       if (data.success) {
//         setSelectedLicense(data.updatedLicense);
//         toast.success('Device removed');
//         getMyGear();
//       } else {
//         toast.error(data.error || 'Failed to remove device');
//       }
//     } catch (err) {
//       toast.error('Error removing device');
//       console.error(err);
//     }
//   };

//   const filteredGear = useMemo(() => {
//     return [...gear]
//       .filter((item) => {
//         if (filter === 'Mac' && !item.macDownloadURI) return false;
//         if (filter === 'Windows' && !item.windowsDownloadURI) return false;
//         return item.name.toLowerCase().includes(searchTerm.toLowerCase());
//       })
//       .sort((a, b) => {
//         if (sortOption === 'nameAsc') return a.name.localeCompare(b.name);
//         if (sortOption === 'nameDesc') return b.name.localeCompare(a.name);
//         return 0;
//       });
//   }, [gear, filter, searchTerm, sortOption]);

//   const getLicenseTypeChip = (license) => {
//     if (!license?.type) return null;

//     if (license.type === 'own') {
//       return (
//         <Chip
//           icon={<WorkspacePremiumRoundedIcon />}
//           label="Owned"
//           size="small"
//           sx={{
//             color: style.color4,
//             backgroundColor: 'rgba(0,180,60,0.18)',
//             border: '1px solid rgba(0,200,80,0.35)',
//             '& .MuiChip-icon': { color: style.color4 },
//           }}
//         />
//       );
//     }

//     if (license.type === 'subscribe') {
//       return (
//         <Chip
//           icon={<AutorenewRoundedIcon />}
//           label="Subscription"
//           size="small"
//           sx={{
//             color: style.color4,
//             backgroundColor: 'rgba(255,255,255,0.05)',
//             border: '1px solid rgba(255,255,255,0.10)',
//             '& .MuiChip-icon': { color: style.color4 },
//           }}
//         />
//       );
//     }

//     return null;
//   };

//   return (
//     <>
//       <Seo
//         title="My Gear | Parish Audio"
//         description="View your Parish Audio plugins, downloads, licenses, and installation details."
//         canonical="https://parishaudio.com/my-gear"
//         robots="noindex,follow"
//       />

//       <Box
//         sx={{
//           width: '95%',
//           maxWidth: 1000,
//           mx: 'auto',
//           py: 4,
//           minHeight: height - 140,
//           overflowX: 'hidden',
//         }}
//       >
//         {isLoading ? (
//           <Box sx={{ textAlign: 'center', mt: 10 }}>
//             <LoadingSpinner />
//           </Box>
//         ) : gear.length === 0 ? (
//           <Paper
//             sx={{
//               p: { xs: 3, md: 5 },
//               borderRadius: 3,
//               backgroundColor: style.color1,
//               color: style.color4,
//               boxShadow: 10,
//               textAlign: 'center',
//             }}
//           >
//             <Typography
//               variant="h1"
//               sx={{
//                 fontSize: { xs: '2rem', md: '2.4rem' },
//                 fontWeight: 800,
//                 mb: 1,
//                 color: style.color4,
//               }}
//             >
//               Your gear library is empty
//             </Typography>

//             <Typography
//               sx={{
//                 color: style.color3,
//                 maxWidth: 620,
//                 mx: 'auto',
//                 lineHeight: 1.8,
//                 mb: 3,
//               }}
//             >
//               When you purchase or subscribe to a plugin, it will appear here along with your
//               download links and device management options.
//             </Typography>

//             <Button
//               component={RouterLink}
//               to="/products"
//               variant="contained"
//               sx={{
//                 backgroundColor: style.color2,
//                 color: style.color4,
//                 fontWeight: 700,
//                 px: 3,
//                 py: 1.2,
//                 '&:hover': {
//                   backgroundColor: style.color2,
//                   filter: 'brightness(1.08)',
//                 },
//               }}
//             >
//               Shop All Plugins
//             </Button>
//           </Paper>
//         ) : (
//           <Stack spacing={3}>
//             <Paper
//               sx={{
//                 p: { xs: 2, md: 2.5 },
//                 borderRadius: 3,
//                 backgroundColor: style.color1,
//                 color: style.color4,
//                 boxShadow: 10,
//               }}
//             >
//               <Stack spacing={2}>
//                 <Box>
//                   <Typography
//                     variant="h1"
//                     sx={{
//                       fontSize: { xs: '2rem', md: '2.5rem' },
//                       fontWeight: 800,
//                       mb: 0.75,
//                       color: style.color4,
//                     }}
//                   >
//                     My Gear
//                   </Typography>
//                 </Box>

//                 <Divider sx={{ backgroundColor: 'rgba(255,255,255,0.08)' }} />

//                 <Stack
//                   direction={{ xs: 'column', md: 'row' }}
//                   spacing={2}
//                   alignItems={{ xs: 'stretch', md: 'center' }}
//                 >
//                   <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 180 } }}>
//                     <InputLabel sx={{ color: style.color4 }}>Platform</InputLabel>
//                     <Select
//                       value={filter}
//                       label="Platform"
//                       onChange={(e) => setFilter(e.target.value)}
//                       sx={{
//                         color: style.color4,
//                         backgroundColor: 'rgba(255,255,255,0.03)',
//                         '.MuiOutlinedInput-notchedOutline': {
//                           borderColor: 'rgba(255,255,255,0.12)',
//                         },
//                         '&:hover .MuiOutlinedInput-notchedOutline': {
//                           borderColor: style.color2,
//                         },
//                         '& .MuiSvgIcon-root': {
//                           color: style.color4,
//                         },
//                       }}
//                     >
//                       <MenuItem value="All">All Platforms</MenuItem>
//                       <MenuItem value="Mac">Mac</MenuItem>
//                       <MenuItem value="Windows">Windows</MenuItem>
//                     </Select>
//                   </FormControl>

//                   <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 180 } }}>
//                     <InputLabel sx={{ color: style.color4 }}>Sort</InputLabel>
//                     <Select
//                       value={sortOption}
//                       label="Sort"
//                       onChange={(e) => setSortOption(e.target.value)}
//                       sx={{
//                         color: style.color4,
//                         backgroundColor: 'rgba(255,255,255,0.03)',
//                         '.MuiOutlinedInput-notchedOutline': {
//                           borderColor: 'rgba(255,255,255,0.12)',
//                         },
//                         '&:hover .MuiOutlinedInput-notchedOutline': {
//                           borderColor: style.color2,
//                         },
//                         '& .MuiSvgIcon-root': {
//                           color: style.color4,
//                         },
//                       }}
//                     >
//                       <MenuItem value="nameAsc">Name A–Z</MenuItem>
//                       <MenuItem value="nameDesc">Name Z–A</MenuItem>
//                     </Select>
//                   </FormControl>

//                   <TextField
//                     size="small"
//                     value={searchTerm}
//                     onChange={(e) => setSearchTerm(e.target.value)}
//                     placeholder="Search your plugins..."
//                     sx={{
//                       flex: 1,
//                       minWidth: { xs: '100%', md: 260 },
//                       backgroundColor: 'rgba(255,255,255,0.03)',
//                       '& .MuiOutlinedInput-root': {
//                         color: style.color4,
//                         '& fieldset': {
//                           borderColor: 'rgba(255,255,255,0.12)',
//                         },
//                         '&:hover fieldset': {
//                           borderColor: style.color2,
//                         },
//                       },
//                       '& input::placeholder': {
//                         color: style.color3,
//                         opacity: 1,
//                       },
//                     }}
//                     InputProps={{
//                       startAdornment: (
//                         <InputAdornment position="start">
//                           <SearchRoundedIcon sx={{ color: style.color3 }} />
//                         </InputAdornment>
//                       ),
//                     }}
//                   />
//                 </Stack>
//               </Stack>
//             </Paper>

//             <Box sx={{ width: '100%', overflowX: 'hidden' }}>
//               <Grid
//                 container
//                 spacing={{ xs: 1.5, sm: 2, md: 2.25 }}
//                 sx={{ width: '100%', mx: 0 }}
//               >
//                 {filteredGear.map((product) => {
//                   const license = getLicenseBySKU(product.SKU);
//                   const machines = license?.machinesInstalledOn || [];

//                   return (
//                     <Grid item xs={12} sm={6} md={4} lg={3} key={product.SKU}>
//                       <Card
//                         sx={{
//                           height: '100%',
//                           display: 'flex',
//                           flexDirection: 'column',
//                           backgroundColor: style.color1,
//                           color: style.color4,
//                           borderRadius: 3,
//                           boxShadow: 8,
//                           overflow: 'hidden',
//                         }}
//                       >
//                         <Box
//                           sx={{
//                             p: 1.5,
//                             minHeight: 190,
//                             display: 'flex',
//                             alignItems: 'center',
//                             justifyContent: 'center',
//                             backgroundColor: style.darkerPanel,
//                           }}
//                         >
//                           <Box
//                             component="a"
//                             href={`/products/${product.productURI}`}
//                             sx={{
//                               display: 'flex',
//                               alignItems: 'center',
//                               justifyContent: 'center',
//                               width: '100%',
//                               height: '100%',
//                               textDecoration: 'none',
//                               //backgroundColor: style.darkPanel,
//                               //p:1,
//                             }}
//                           >
//                             <Box
//                               component="img"
//                               src={product.images?.[0]?.URI || '/default-image.png'}
//                               alt={product.images?.[0]?.altText || 'Product image'}
//                               sx={{
//                                 maxHeight: 165,
//                                 maxWidth: '100%',
//                                 objectFit: 'contain',
//                                 display: 'block',
//                               }}
//                             />
//                           </Box>
//                         </Box>

//                         <CardContent
//                           sx={{
//                             display: 'flex',
//                             flexDirection: 'column',
//                             flex: 1,
//                             p: 1.75,
//                             '&:last-child': { pb: 1.75 },
//                           }}
//                         >
//                           <Stack spacing={1.25} sx={{ flex: 1 }}>
//                             <Box>
//                               <Typography
//                                 component="a"
//                                 href={`/products/${product.productURI}`}
//                                 sx={{
//                                   color: style.color4,
//                                   fontSize: '1.05rem',
//                                   fontWeight: 800,
//                                   textDecoration: 'none',
//                                   lineHeight: 1.35,
//                                   '&:hover': { color: style.color3 },
//                                 }}
//                               >
//                                 {product.name}
//                               </Typography>
//                             </Box>

//                             <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
//                               {getLicenseTypeChip(license)}
//                               <Chip
//                                 icon={<DevicesRoundedIcon />}
//                                 label={`${machines.length} device${machines.length !== 1 ? 's' : ''}`}
//                                 size="small"
//                                 sx={{
//                                   color: style.color4,
//                                   backgroundColor: 'rgba(255,255,255,0.05)',
//                                   border: '1px solid rgba(255,255,255,0.08)',
//                                   '& .MuiChip-icon': { color: style.color4 },
//                                 }}
//                               />
//                             </Stack>
//                           </Stack>

//                           <Divider sx={{ my: 1.5, backgroundColor: 'rgba(255,255,255,0.08)' }} />

//                           <Stack spacing={0.5}>
//                             <Typography sx={{ color: style.color4, fontWeight: 700, fontSize: '0.95rem' }}>
//                               Download:
//                             </Typography>

//                             <Stack direction="row" spacing={0.5}>
//                               {product.windowsDownloadURI && (
//                                 <Button
//                                   href={product.windowsDownloadURI}
//                                   target="_blank"
//                                   rel="noopener noreferrer"
//                                   variant="outlined"
//                                   startIcon={<DesktopWindowsRoundedIcon />}
//                                   sx={{
//                                     flex: 1,
//                                     minWidth: 0,
//                                     borderColor: style.color2,
//                                     color: style.color4,
//                                     fontWeight: 700,
//                                     py: 0.9,
//                                     fontSize: '0.84rem',
//                                     '&:hover': {
//                                       borderColor: style.color4,
//                                       backgroundColor: 'rgba(255,255,255,0.04)',
//                                     },
//                                   }}
//                                 >
//                                   Windows
//                                 </Button>
//                               )}

//                               {product.macDownloadURI && (
//                                 <Button
//                                   href={product.macDownloadURI}
//                                   target="_blank"
//                                   rel="noopener noreferrer"
//                                   variant="outlined"
//                                   startIcon={<AppleIcon />}
//                                   sx={{
//                                     flex: 1,
//                                     minWidth: 0,
//                                     borderColor: style.color2,
//                                     color: style.color4,
//                                     fontWeight: 700,
//                                     py: 0.9,
//                                     fontSize: '0.84rem',
//                                     '&:hover': {
//                                       borderColor: style.color4,
//                                       backgroundColor: 'rgba(255,255,255,0.04)',
//                                     },
//                                   }}
//                                 >
//                                   Mac
//                                 </Button>
//                               )}
//                             </Stack>

//                             <Button
//                               variant="text"
//                               startIcon={<DevicesRoundedIcon />}
//                               onClick={() => handleOpenDevicesModal(license)}
//                               disabled={!license || machines.length === 0}
//                               sx={{
//                                 alignSelf: 'flex-start',
//                                 color: style.color3,
//                                 fontWeight: 700,
//                                 fontSize: '0.85rem',
//                                 px: 0,
//                                 '&.Mui-disabled': {
//                                   color: 'rgba(255,255,255,0.28)',
//                                 },
//                                 '&:hover': {
//                                   backgroundColor: 'transparent',
//                                   color: style.color4,
//                                 },
//                               }}
//                             >
//                               {machines.length > 0 ? 'Manage Devices' : 'No Devices Yet'}
//                             </Button>
//                           </Stack>
//                         </CardContent>
//                       </Card>
//                     </Grid>
//                   );
//                 })}
//               </Grid>
//             </Box>
//           </Stack>
//         )}

//         <Dialog
//           open={isModalOpen}
//           onClose={handleCloseDevicesModal}
//           fullWidth
//           maxWidth="md"
//           PaperProps={{
//             sx: {
//               borderRadius: 3,
//               overflow: 'hidden',
//               backgroundColor: style.color1,
//               color: style.color4,
//             },
//           }}
//         >
//           <DialogTitle
//             sx={{
//               display: 'flex',
//               justifyContent: 'space-between',
//               alignItems: 'center',
//               backgroundColor: style.color2,
//               color: style.color4,
//               py: 1.5,
//             }}
//           >
//             <Box>
//               <Typography sx={{ fontWeight: 800, fontSize: '1.15rem' }}>
//                 {selectedLicense?.productName || 'Manage Devices'}
//               </Typography>
//               <Typography sx={{ fontSize: '0.92rem', opacity: 0.9 }}>
//                 Devices currently authorized for this plugin
//               </Typography>
//             </Box>

//             <IconButton onClick={handleCloseDevicesModal} sx={{ color: style.color4 }}>
//               <CloseIcon />
//             </IconButton>
//           </DialogTitle>

//           <DialogContent dividers sx={{ backgroundColor: style.color1, color: style.color4, p: 2.5 }}>
//             <Stack spacing={2}>
//               {!selectedLicense?.machinesInstalledOn?.length ? (
//                 <Typography sx={{ color: style.color3 }}>
//                   No active devices were found for this license yet.
//                 </Typography>
//               ) : (
//                 selectedLicense.machinesInstalledOn.map((device) => (
//                   <Paper
//                     key={device.ipAddress}
//                     sx={{
//                       p: 2,
//                       borderRadius: 2.5,
//                       backgroundColor: 'rgba(255,255,255,0.035)',
//                       boxShadow: 3,
//                     }}
//                   >
//                     <Grid container spacing={2} alignItems="center">
//                       <Grid item xs={12} md={3}>
//                         <Typography sx={{ color: style.color3, fontSize: '0.86rem', mb: 0.25 }}>
//                           IP Address
//                         </Typography>
//                         <Typography sx={{ color: style.color4, fontWeight: 700 }}>
//                           {device.ipAddress}
//                         </Typography>
//                       </Grid>

//                       <Grid item xs={12} md={5}>
//                         <Typography sx={{ color: style.color3, fontSize: '0.86rem', mb: 0.25 }}>
//                           Device
//                         </Typography>
//                         <Typography sx={{ color: style.color4, fontWeight: 700 }}>
//                           {device.deviceName || 'Unknown Device'}
//                         </Typography>
//                         <Typography sx={{ color: style.color3, fontSize: '0.9rem' }}>
//                           {device.osName || 'Unknown OS'}
//                         </Typography>
//                       </Grid>

//                       <Grid item xs={12} md={4}>
//                         <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
//                           <Button
//                             variant="outlined"
//                             onClick={() => handleRemoveDevice(device.ipAddress)}
//                             sx={{
//                               color: style.color4,
//                               borderColor: style.color2,
//                               fontWeight: 700,
//                               minWidth: 130,
//                               '&:hover': {
//                                 borderColor: style.color4,
//                                 backgroundColor: 'rgba(255,255,255,0.04)',
//                               },
//                             }}
//                           >
//                             Remove Device
//                           </Button>
//                         </Box>
//                       </Grid>
//                     </Grid>
//                   </Paper>
//                 ))
//               )}
//             </Stack>
//           </DialogContent>

//           <DialogActions sx={{ backgroundColor: style.color1, px: 2.5, pb: 2.5 }}>
//             <Button
//               onClick={handleCloseDevicesModal}
//               variant="contained"
//               sx={{
//                 backgroundColor: style.color2,
//                 color: style.color4,
//                 fontWeight: 700,
//                 px: 3,
//                 '&:hover': {
//                   backgroundColor: style.color2,
//                   filter: 'brightness(1.08)',
//                 },
//               }}
//             >
//               Close
//             </Button>
//           </DialogActions>
//         </Dialog>
//       </Box>
//     </>
//   );
// }

// import React, { useState, useEffect, useMemo } from 'react';
// import {
//   Box,
//   Grid,
//   Card,
//   CardContent,
//   Typography,
//   Button,
//   Select,
//   MenuItem,
//   FormControl,
//   InputLabel,
//   TextField,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   Divider,
//   IconButton,
//   Stack,
//   Paper,
//   Chip,
//   InputAdornment,
// } from '@mui/material';
// import CloseIcon from '@mui/icons-material/Close';
// import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
// import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
// import DevicesRoundedIcon from '@mui/icons-material/DevicesRounded';
// import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
// import DesktopWindowsRoundedIcon from '@mui/icons-material/DesktopWindowsRounded';
// import AppleIcon from '@mui/icons-material/Apple';
// import ExtensionRoundedIcon from '@mui/icons-material/ExtensionRounded';
// import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
// import { useStyle } from '../../context/styleContext/styleContext';
// import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
// import axios from 'axios';
// import { toast } from 'react-hot-toast';
// import Seo from '../../components/Seo';
// import { Link as RouterLink } from 'react-router-dom';

// export default function MyGear() {
//   const { style } = useStyle();
//   const [gear, setGear] = useState([]);
//   const [licenses, setLicenses] = useState([]);
//   const [isLoading, setIsLoading] = useState(true);
//   const [filter, setFilter] = useState('All');
//   const [searchTerm, setSearchTerm] = useState('');
//   const [sortOption, setSortOption] = useState('nameAsc');
//   const [selectedLicense, setSelectedLicense] = useState(null);
//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [height, setHeight] = useState(window.innerHeight);

//   useEffect(() => {
//     function handleResize() {
//       setHeight(window.innerHeight);
//     }

//     window.addEventListener('resize', handleResize);
//     getMyGear();

//     return () => {
//       window.removeEventListener('resize', handleResize);
//     };
//   }, []);

//   const getMyGear = async () => {
//     const token = localStorage.getItem('token');

//     try {
//       const { data } = await axios.post('/api/get_my_gear', { token });

//       if (data.error) {
//         toast.error(data.error);
//       } else {
//         setGear(data.products || []);
//         setLicenses(data.licenses || []);
//       }
//     } catch (error) {
//       console.log(error);
//       toast.error('Unable to load your gear right now.');
//     }

//     setIsLoading(false);
//   };

//   const getLicenseBySKU = (sku) =>
//     licenses.find((l) => l.sku === sku || l.SKU === sku);

//   const handleOpenDevicesModal = (license) => {
//     setSelectedLicense(license);
//     setIsModalOpen(true);
//   };

//   const handleCloseDevicesModal = () => {
//     setSelectedLicense(null);
//     setIsModalOpen(false);
//   };

//   const handleRemoveDevice = async (deviceId) => {
//     const token = localStorage.getItem('token');

//     try {
//       const { data } = await axios.post('/api/remove_device_from_license', {
//         token,
//         licenseID: selectedLicense._id,
//         ipAddress: deviceId,
//       });

//       if (data.success) {
//         setSelectedLicense(data.updatedLicense);
//         toast.success('Device removed');
//         getMyGear();
//       } else {
//         toast.error(data.error || 'Failed to remove device');
//       }
//     } catch (err) {
//       toast.error('Error removing device');
//       console.error(err);
//     }
//   };

//   const filteredGear = useMemo(() => {
//     return [...gear]
//       .filter((item) => {
//         if (filter === 'Mac' && !item.macDownloadURI) return false;
//         if (filter === 'Windows' && !item.windowsDownloadURI) return false;
//         return item.name.toLowerCase().includes(searchTerm.toLowerCase());
//       })
//       .sort((a, b) => {
//         if (sortOption === 'nameAsc') return a.name.localeCompare(b.name);
//         if (sortOption === 'nameDesc') return b.name.localeCompare(a.name);
//         return 0;
//       });
//   }, [gear, filter, searchTerm, sortOption]);

//   const totalDevices = licenses.reduce((sum, license) => {
//     const machines = license?.machinesInstalledOn || [];
//     return sum + machines.length;
//   }, 0);

//   return (
//     <>
//       <Seo
//         title="My Gear | Parish Audio"
//         description="View your Parish Audio plugins, downloads, licenses, and installation details."
//         canonical="https://parishaudio.com/my-gear"
//         robots="noindex,follow"
//       />

//       <Box sx={{ width: '95%', maxWidth: 1200, mx: 'auto', py: 4, minHeight: height - 140 }}>
//         {isLoading ? (
//           <Box sx={{ textAlign: 'center', mt: 10 }}>
//             <LoadingSpinner />
//           </Box>
//         ) : gear.length === 0 ? (
//           <Paper
//             sx={{
//               p: { xs: 3, md: 5 },
//               borderRadius: 3,
//               backgroundColor: style.color1,
//               color: style.color4,
//               boxShadow: 10,
//               textAlign: 'center',
//             }}
//           >
//             <ExtensionRoundedIcon sx={{ fontSize: 56, color: style.color2, mb: 1 }} />
//             <Typography
//               variant="h1"
//               sx={{
//                 fontSize: { xs: '2rem', md: '2.4rem' },
//                 fontWeight: 800,
//                 mb: 1,
//                 color: style.color4,
//               }}
//             >
//               Your gear library is empty
//             </Typography>
//             <Typography
//               sx={{
//                 color: style.color3,
//                 maxWidth: 620,
//                 mx: 'auto',
//                 lineHeight: 1.8,
//                 mb: 3,
//               }}
//             >
//               When you purchase or claim a plugin, it will appear here along with your
//               download links and device management options.
//             </Typography>

//             <Button
//               component={RouterLink}
//               to="/plugins"
//               variant="contained"
//               sx={{
//                 backgroundColor: style.color2,
//                 color: style.color4,
//                 fontWeight: 700,
//                 px: 3,
//                 py: 1.2,
//                 '&:hover': {
//                   backgroundColor: style.color2,
//                   filter: 'brightness(1.08)',
//                 },
//               }}
//             >
//               Shop All Plugins
//             </Button>
//           </Paper>
//         ) : (
//           <Stack spacing={3}>
//             <Paper
//               sx={{
//                 p: { xs: 2.5, md: 3 },
//                 borderRadius: 3,
//                 backgroundColor: style.color1,
//                 color: style.color4,
//                 boxShadow: 10,
//               }}
//             >
//               <Stack spacing={2.5}>
//                 <Box>
//                   <Typography
//                     variant="h1"
//                     sx={{
//                       fontSize: { xs: '2rem', md: '2.8rem' },
//                       fontWeight: 800,
//                       mb: 1,
//                       color: style.color4,
//                     }}
//                   >
//                     My Gear
//                   </Typography>

//                   <Typography
//                     sx={{
//                       color: style.color3,
//                       lineHeight: 1.8,
//                       maxWidth: 800,
//                     }}
//                   >
//                     Download your plugins, view your active devices, and manage your licenses
//                     from one place.
//                   </Typography>
//                 </Box>

//                 <Grid container spacing={2}>
//                   <Grid item xs={12} md={4}>
//                     <Paper
//                       sx={{
//                         p: 2,
//                         borderRadius: 2.5,
//                         backgroundColor: 'rgba(255,255,255,0.035)',
//                         boxShadow: 4,
//                         height: '100%',
//                       }}
//                     >
//                       <Typography sx={{ color: style.color3, fontSize: '0.95rem' }}>
//                         Plugins in your library
//                       </Typography>
//                       <Typography sx={{ color: style.color4, fontSize: '2rem', fontWeight: 900 }}>
//                         {gear.length}
//                       </Typography>
//                     </Paper>
//                   </Grid>

//                   <Grid item xs={12} md={4}>
//                     <Paper
//                       sx={{
//                         p: 2,
//                         borderRadius: 2.5,
//                         backgroundColor: 'rgba(255,255,255,0.035)',
//                         boxShadow: 4,
//                         height: '100%',
//                       }}
//                     >
//                       <Typography sx={{ color: style.color3, fontSize: '0.95rem' }}>
//                         Active licenses
//                       </Typography>
//                       <Typography sx={{ color: style.color4, fontSize: '2rem', fontWeight: 900 }}>
//                         {licenses.length}
//                       </Typography>
//                     </Paper>
//                   </Grid>

//                   <Grid item xs={12} md={4}>
//                     <Paper
//                       sx={{
//                         p: 2,
//                         borderRadius: 2.5,
//                         backgroundColor: 'rgba(255,255,255,0.035)',
//                         boxShadow: 4,
//                         height: '100%',
//                       }}
//                     >
//                       <Typography sx={{ color: style.color3, fontSize: '0.95rem' }}>
//                         Total connected devices
//                       </Typography>
//                       <Typography sx={{ color: style.color4, fontSize: '2rem', fontWeight: 900 }}>
//                         {totalDevices}
//                       </Typography>
//                     </Paper>
//                   </Grid>
//                 </Grid>

//                 <Divider sx={{ backgroundColor: 'rgba(255,255,255,0.08)' }} />

//                 <Stack
//                   direction={{ xs: 'column', md: 'row' }}
//                   spacing={2}
//                   alignItems={{ xs: 'stretch', md: 'center' }}
//                 >
//                   <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 180 } }}>
//                     <InputLabel sx={{ color: style.color4 }}>Platform</InputLabel>
//                     <Select
//                       value={filter}
//                       label="Platform"
//                       onChange={(e) => setFilter(e.target.value)}
//                       sx={{
//                         color: style.color4,
//                         backgroundColor: 'rgba(255,255,255,0.03)',
//                         '.MuiOutlinedInput-notchedOutline': {
//                           borderColor: 'rgba(255,255,255,0.12)',
//                         },
//                         '&:hover .MuiOutlinedInput-notchedOutline': {
//                           borderColor: style.color2,
//                         },
//                         '& .MuiSvgIcon-root': {
//                           color: style.color4,
//                         },
//                       }}
//                     >
//                       <MenuItem value="All">All Platforms</MenuItem>
//                       <MenuItem value="Mac">Mac</MenuItem>
//                       <MenuItem value="Windows">Windows</MenuItem>
//                     </Select>
//                   </FormControl>

//                   <FormControl size="small" sx={{ minWidth: { xs: '100%', md: 180 } }}>
//                     <InputLabel sx={{ color: style.color4 }}>Sort</InputLabel>
//                     <Select
//                       value={sortOption}
//                       label="Sort"
//                       onChange={(e) => setSortOption(e.target.value)}
//                       sx={{
//                         color: style.color4,
//                         backgroundColor: 'rgba(255,255,255,0.03)',
//                         '.MuiOutlinedInput-notchedOutline': {
//                           borderColor: 'rgba(255,255,255,0.12)',
//                         },
//                         '&:hover .MuiOutlinedInput-notchedOutline': {
//                           borderColor: style.color2,
//                         },
//                         '& .MuiSvgIcon-root': {
//                           color: style.color4,
//                         },
//                       }}
//                     >
//                       <MenuItem value="nameAsc">Name A–Z</MenuItem>
//                       <MenuItem value="nameDesc">Name Z–A</MenuItem>
//                     </Select>
//                   </FormControl>

//                   <TextField
//                     size="small"
//                     value={searchTerm}
//                     onChange={(e) => setSearchTerm(e.target.value)}
//                     placeholder="Search your plugins..."
//                     sx={{
//                       flex: 1,
//                       minWidth: { xs: '100%', md: 260 },
//                       backgroundColor: 'rgba(255,255,255,0.03)',
//                       '& .MuiOutlinedInput-root': {
//                         color: style.color4,
//                         '& fieldset': {
//                           borderColor: 'rgba(255,255,255,0.12)',
//                         },
//                         '&:hover fieldset': {
//                           borderColor: style.color2,
//                         },
//                       },
//                       '& input::placeholder': {
//                         color: style.color3,
//                         opacity: 1,
//                       },
//                     }}
//                     InputProps={{
//                       startAdornment: (
//                         <InputAdornment position="start">
//                           <SearchRoundedIcon sx={{ color: style.color3 }} />
//                         </InputAdornment>
//                       ),
//                     }}
//                   />
//                 </Stack>
//               </Stack>
//             </Paper>

//             <Grid container spacing={3}>
//               {filteredGear.map((product) => {
//                 const license = getLicenseBySKU(product.SKU);
//                 const machines = license?.machinesInstalledOn || [];

//                 return (
//                   <Grid item xs={12} sm={6} lg={4} key={product.SKU}>
//                     <Card
//                       sx={{
//                         height: '100%',
//                         display: 'flex',
//                         flexDirection: 'column',
//                         backgroundColor: style.color1,
//                         color: style.color4,
//                         borderRadius: 3,
//                         boxShadow: 10,
//                         overflow: 'hidden',
//                       }}
//                     >
//                       <Box
//                         sx={{
//                           p: 2,
//                           minHeight: 220,
//                           display: 'flex',
//                           alignItems: 'center',
//                           justifyContent: 'center',
//                           backgroundColor: 'rgba(255,255,255,0.025)',
//                         }}
//                       >
//                         <Box
//                           component="a"
//                           href={`/products/${product.productURI}`}
//                           sx={{
//                             display: 'flex',
//                             alignItems: 'center',
//                             justifyContent: 'center',
//                             width: '100%',
//                             height: '100%',
//                             textDecoration: 'none',
//                           }}
//                         >
//                           <Box
//                             component="img"
//                             src={product.images?.[0]?.URI || '/default-image.png'}
//                             alt={product.images?.[0]?.altText || 'Product image'}
//                             sx={{
//                               maxHeight: 180,
//                               maxWidth: '100%',
//                               objectFit: 'contain',
//                               display: 'block',
//                             }}
//                           />
//                         </Box>
//                       </Box>

//                       <CardContent
//                         sx={{
//                           display: 'flex',
//                           flexDirection: 'column',
//                           flex: 1,
//                           p: 2.25,
//                           '&:last-child': { pb: 2.25 },
//                         }}
//                       >
//                         <Stack spacing={1.5} sx={{ flex: 1 }}>
//                           <Box>
//                             <Typography
//                               component="a"
//                               href={`/products/${product.productURI}`}
//                               sx={{
//                                 color: style.color4,
//                                 fontSize: '1.2rem',
//                                 fontWeight: 800,
//                                 textDecoration: 'none',
//                                 '&:hover': { color: style.color3 },
//                               }}
//                             >
//                               {product.name}
//                             </Typography>

//                             <Typography
//                               sx={{
//                                 mt: 0.8,
//                                 color: style.color3,
//                                 lineHeight: 1.7,
//                                 fontSize: '0.94rem',
//                               }}
//                             >
//                               {product.shortDescription || product.cardSummary || 'Audio plugin'}
//                             </Typography>
//                           </Box>

//                           <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
//                             <Chip
//                               icon={<CheckCircleRoundedIcon />}
//                               label="Licensed"
//                               size="small"
//                               sx={{
//                                 color: style.color4,
//                                 backgroundColor: 'rgba(0,180,60,0.18)',
//                                 border: '1px solid rgba(0,200,80,0.35)',
//                                 '& .MuiChip-icon': { color: style.color4 },
//                               }}
//                             />

//                             <Chip
//                               icon={<DevicesRoundedIcon />}
//                               label={`${machines.length} device${machines.length !== 1 ? 's' : ''}`}
//                               size="small"
//                               sx={{
//                                 color: style.color4,
//                                 backgroundColor: 'rgba(255,255,255,0.05)',
//                                 border: '1px solid rgba(255,255,255,0.08)',
//                                 '& .MuiChip-icon': { color: style.color4 },
//                               }}
//                             />
//                           </Stack>

//                           <Paper
//                             sx={{
//                               p: 1.5,
//                               borderRadius: 2,
//                               backgroundColor: 'rgba(255,255,255,0.035)',
//                               boxShadow: 2,
//                             }}
//                           >
//                             <Stack direction="row" spacing={1} alignItems="flex-start">
//                               <InfoOutlinedIcon sx={{ color: style.color2, mt: '2px', fontSize: '1.1rem' }} />
//                               <Typography sx={{ color: style.color3, fontSize: '0.92rem', lineHeight: 1.65 }}>
//                                 Download and install the plugin, then open it in your DAW and
//                                 sign in with your Parish Audio account to unlock it.
//                               </Typography>
//                             </Stack>
//                           </Paper>
//                         </Stack>

//                         <Divider sx={{ my: 2, backgroundColor: 'rgba(255,255,255,0.08)' }} />

//                         <Stack spacing={1.25}>
//                           <Typography sx={{ color: style.color4, fontWeight: 700 }}>
//                             Downloads
//                           </Typography>

//                           <Stack direction="row" spacing={1.2}>
//                             {product.windowsDownloadURI && (
//                               <Button
//                                 href={product.windowsDownloadURI}
//                                 target="_blank"
//                                 rel="noopener noreferrer"
//                                 variant="outlined"
//                                 startIcon={<DesktopWindowsRoundedIcon />}
//                                 sx={{
//                                   flex: 1,
//                                   borderColor: style.color2,
//                                   color: style.color4,
//                                   fontWeight: 700,
//                                   py: 1.1,
//                                   '&:hover': {
//                                     borderColor: style.color4,
//                                     backgroundColor: 'rgba(255,255,255,0.04)',
//                                   },
//                                 }}
//                               >
//                                 Windows
//                               </Button>
//                             )}

//                             {product.macDownloadURI && (
//                               <Button
//                                 href={product.macDownloadURI}
//                                 target="_blank"
//                                 rel="noopener noreferrer"
//                                 variant="outlined"
//                                 startIcon={<AppleIcon />}
//                                 sx={{
//                                   flex: 1,
//                                   borderColor: style.color2,
//                                   color: style.color4,
//                                   fontWeight: 700,
//                                   py: 1.1,
//                                   '&:hover': {
//                                     borderColor: style.color4,
//                                     backgroundColor: 'rgba(255,255,255,0.04)',
//                                   },
//                                 }}
//                               >
//                                 Mac
//                               </Button>
//                             )}
//                           </Stack>

//                           <Button
//                             variant="text"
//                             startIcon={<DevicesRoundedIcon />}
//                             onClick={() => handleOpenDevicesModal(license)}
//                             disabled={!license || machines.length === 0}
//                             sx={{
//                               alignSelf: 'flex-start',
//                               color: style.color3,
//                               fontWeight: 700,
//                               px: 0,
//                               '&.Mui-disabled': {
//                                 color: 'rgba(255,255,255,0.28)',
//                               },
//                               '&:hover': {
//                                 backgroundColor: 'transparent',
//                                 color: style.color4,
//                               },
//                             }}
//                           >
//                             {machines.length > 0 ? 'Manage Devices' : 'No Devices Yet'}
//                           </Button>
//                         </Stack>
//                       </CardContent>
//                     </Card>
//                   </Grid>
//                 );
//               })}
//             </Grid>
//           </Stack>
//         )}

//         <Dialog
//           open={isModalOpen}
//           onClose={handleCloseDevicesModal}
//           fullWidth
//           maxWidth="md"
//           PaperProps={{
//             sx: {
//               borderRadius: 3,
//               overflow: 'hidden',
//               backgroundColor: style.color1,
//               color: style.color4,
//             },
//           }}
//         >
//           <DialogTitle
//             sx={{
//               display: 'flex',
//               justifyContent: 'space-between',
//               alignItems: 'center',
//               backgroundColor: style.color2,
//               color: style.color4,
//               py: 1.5,
//             }}
//           >
//             <Box>
//               <Typography sx={{ fontWeight: 800, fontSize: '1.15rem' }}>
//                 {selectedLicense?.productName || 'Manage Devices'}
//               </Typography>
//               <Typography sx={{ fontSize: '0.92rem', opacity: 0.9 }}>
//                 Devices currently authorized for this plugin
//               </Typography>
//             </Box>

//             <IconButton onClick={handleCloseDevicesModal} sx={{ color: style.color4 }}>
//               <CloseIcon />
//             </IconButton>
//           </DialogTitle>

//           <DialogContent dividers sx={{ backgroundColor: style.color1, color: style.color4, p: 2.5 }}>
//             <Stack spacing={2}>
//               {!selectedLicense?.machinesInstalledOn?.length ? (
//                 <Typography sx={{ color: style.color3 }}>
//                   No active devices were found for this license yet.
//                 </Typography>
//               ) : (
//                 selectedLicense.machinesInstalledOn.map((device) => (
//                   <Paper
//                     key={device.ipAddress}
//                     sx={{
//                       p: 2,
//                       borderRadius: 2.5,
//                       backgroundColor: 'rgba(255,255,255,0.035)',
//                       boxShadow: 3,
//                     }}
//                   >
//                     <Grid container spacing={2} alignItems="center">
//                       <Grid item xs={12} md={3}>
//                         <Typography sx={{ color: style.color3, fontSize: '0.86rem', mb: 0.25 }}>
//                           IP Address
//                         </Typography>
//                         <Typography sx={{ color: style.color4, fontWeight: 700 }}>
//                           {device.ipAddress}
//                         </Typography>
//                       </Grid>

//                       <Grid item xs={12} md={5}>
//                         <Typography sx={{ color: style.color3, fontSize: '0.86rem', mb: 0.25 }}>
//                           Device
//                         </Typography>
//                         <Typography sx={{ color: style.color4, fontWeight: 700 }}>
//                           {device.deviceName || 'Unknown Device'}
//                         </Typography>
//                         <Typography sx={{ color: style.color3, fontSize: '0.9rem' }}>
//                           {device.osName || 'Unknown OS'}
//                         </Typography>
//                       </Grid>

//                       <Grid item xs={12} md={4}>
//                         <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
//                           <Button
//                             variant="outlined"
//                             onClick={() => handleRemoveDevice(device.ipAddress)}
//                             sx={{
//                               color: style.color4,
//                               borderColor: style.color2,
//                               fontWeight: 700,
//                               minWidth: 130,
//                               '&:hover': {
//                                 borderColor: style.color4,
//                                 backgroundColor: 'rgba(255,255,255,0.04)',
//                               },
//                             }}
//                           >
//                             Remove Device
//                           </Button>
//                         </Box>
//                       </Grid>
//                     </Grid>
//                   </Paper>
//                 ))
//               )}
//             </Stack>
//           </DialogContent>

//           <DialogActions sx={{ backgroundColor: style.color1, px: 2.5, pb: 2.5 }}>
//             <Button
//               onClick={handleCloseDevicesModal}
//               variant="contained"
//               sx={{
//                 backgroundColor: style.color2,
//                 color: style.color4,
//                 fontWeight: 700,
//                 px: 3,
//                 '&:hover': {
//                   backgroundColor: style.color2,
//                   filter: 'brightness(1.08)',
//                 },
//               }}
//             >
//               Close
//             </Button>
//           </DialogActions>
//         </Dialog>
//       </Box>
//     </>
//   );
// }

// import React, { useState, useEffect } from 'react';
// import {
//   Box,
//   Grid,
//   Card,
//   CardContent,
//   CardMedia,
//   Typography,
//   Button,
//   Select,
//   MenuItem,
//   FormControl,
//   InputLabel,
//   TextField,
//   Dialog,
//   DialogTitle,
//   DialogContent,
//   DialogActions,
//   Divider,
//   IconButton
// } from '@mui/material';
// import CloseIcon from '@mui/icons-material/Close';
// import { useStyle } from '../../context/styleContext/styleContext';
// import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
// import axios from 'axios';
// import { toast } from 'react-hot-toast';
// import Seo from '../../components/Seo';


// export default function MyGear() {
//   const { style } = useStyle();
//   const [gear, setGear] = useState([]);
//   const [licenses, setLicenses] = useState([]);
//   const [isLoading, setIsLoading] = useState(true);
//   const [filter, setFilter] = useState('All');
//   const [searchTerm, setSearchTerm] = useState('');
//   const [sortOption, setSortOption] = useState('nameAsc');
//   const [selectedLicense, setSelectedLicense] = useState(null);
//   const [isModalOpen, setIsModalOpen] = useState(false);

//   const [width, setWidth] = useState(window.innerWidth


//   )
// function handleWindowSizeChange() {
//     setWidth(window.innerWidth);
// }
// useEffect(() => {
//     window.addEventListener('resize', handleWindowSizeChange);
//     getMyGear();
    
//     return () => {
//         window.removeEventListener('resize', handleWindowSizeChange);
//     }
// }, []);

// const isMobile = width <= 768;


//   const getMyGear = async () => {
//     const token = localStorage.getItem('token');
//     try {
//       const { data } = await axios.post('/api/get_my_gear', { token });
//       if (data.error) {
//         toast.error(data.error);
//       } else {
//         setGear(data.products);
//         setLicenses(data.licenses);
//       }
//     } catch (error) {
//       console.log(error);
//     }
//     setIsLoading(false);
//   };

//   const getLicenseBySKU = (sku) => licenses.find((l) => l.sku === sku);

//   const handleOpenDevicesModal = (license) => {
//     setSelectedLicense(license);
//     setIsModalOpen(true);
//   };

//   const handleCloseDevicesModal = () => {
//     setSelectedLicense(null);
//     setIsModalOpen(false);
//   };

//   const handleRemoveDevice = async (deviceId) => {
//     const token = localStorage.getItem('token');
// console.log("IP?????")
// console.log(selectedLicense.ipAddress);
//     try {
//       console.log(selectedLicense)

//       const { data } = await axios.post('/api/remove_device_from_license', {
//         token,
//         licenseID: selectedLicense._id,
//         ipAddress: deviceId,
//       });
//       if (data.success) {
//         console.log("Updated license")
//         console.log(data.updatedLicense);
//         console.log("XXXXX")
//         setSelectedLicense(data.updatedLicense)
//         toast.success('Device removed');
//         getMyGear();
//         //handleCloseDevicesModal();
//       } else {
//         toast.error(data.error || 'Failed to remove device');
//       }
//     } catch (err) {
//       toast.error('Error removing device');
//       console.error(err);
//     }
//   };

//   const filteredGear = gear
//     .filter((item) => {
//       if (filter === 'Mac' && !item.macDownloadURI) return false;
//       if (filter === 'Windows' && !item.windowsDownloadURI) return false;
//       return item.name.toLowerCase().includes(searchTerm.toLowerCase());
//     })
//     .sort((a, b) => {
//       if (sortOption === 'nameAsc') return a.name.localeCompare(b.name);
//       if (sortOption === 'nameDesc') return b.name.localeCompare(a.name);
//       return 0;
//     });

//   return (
//     <>
//     <Seo
//   title="My Gear | Parish Audio"
//   description="View your Parish Audio plugins, downloads, licenses, and installation details."
//   canonical="https://parishaudio.com/my-gear"
//   robots="noindex,follow"
// />
    
//     <div className='image-background-1'>
//       <div className='dark-alpha-80'>
//         <Box sx={{ minHeight: '100vh', color: style.color4 }}>
//           {isLoading ? (
//             <Box sx={{ textAlign: 'center', mt: 10 }}><LoadingSpinner /></Box>
//           ) : (
//             <>
//               {gear.length === 0 ? (
//                 <Box textAlign="center" mt={10}>
//                   <Typography variant="h5">You do not yet have any equipment.</Typography>
//                   <Box mt={3}>
//                     <a href="/plugins" style={{ color: style.color3 }}>
//                       <Typography variant="h6">Shop All Plugins</Typography>
//                     </a>
//                   </Box>
//                 </Box>
//               ) : (
//                 <Box sx={{ maxWidth: 1200, mx: 'auto', p: 2 }}>
//                   <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
//                     <FormControl sx={{ minWidth: 180 }}>
//                       <InputLabel sx={{ color: style.color4 }}>Sort</InputLabel>
//                       <Select
//                         value={sortOption}
//                         onChange={(e) => setSortOption(e.target.value)}
//                         size="small"
//                         sx={{ color: style.color4, backgroundColor: style.color1 }}
//                       >
//                         <MenuItem value="nameAsc">Name A–Z</MenuItem>
//                         <MenuItem value="nameDesc">Name Z–A</MenuItem>
//                       </Select>
//                     </FormControl>

//                     <TextField
//                       variant="outlined"
//                       placeholder="Search by name..."
//                       value={searchTerm}
//                       size="small"
//                       onChange={(e) => setSearchTerm(e.target.value)}
//                       sx={{ minWidth: 250, backgroundColor: style.color1, input: { color: style.color4 } }}
//                     />
//                   </Box>

//                   <Grid container spacing={3}>
//                     {filteredGear.map((product) => {
//                       const license = getLicenseBySKU(product.SKU);
//                       const machines = license?.machinesInstalledOn || [];

//                       return (
//                         <Grid item xs={12} sm={6} md={4} key={product.SKU}>
//                           <Card sx={{ backgroundColor: style.color1, color: style.color4 }}>
//                             <a href={`/products/${product.productURI}`}>
//                               <CardMedia
//                                 component="img"
//                                 image={product.images?.[0]?.URI || '/default-image.png'}
//                                 alt={product.images?.[0]?.altText || 'Product image'}
//                                 sx={{ maxHeight: 200, maxWidth:200, objectFit: 'contain', p: 0, margin:'auto',  mt:2 }}
//                               />
//                             </a>
//                             <CardContent>
//                               <Typography variant="h6">
//                                 <a href={`/products/${product.productURI}`} style={{ color: style.color4 }}><strong>{product.name}</strong></a>
//                               </Typography>
//                               <Divider className='divider1' />
//                               <div style={{display:'flex', flexDirection:'row' }}>
//                                 <Typography variant="body1" sx={{ mt: 1 }}>

//                                 Installed on {machines.length} device{machines.length !== 1 ? 's' : ''}
//                               </Typography>
//                               {machines.length > 0 && (
//                                 <Button variant="outlined" size="small" onClick={() => handleOpenDevicesModal(license)} sx={{ mt: 1, ml: 1, borderColor:style.color2, backgroundColor: style.color1, color:style.color4, '&:hover': {backgroundColor: style.color2, color:'white' } }}>
//                                   Manage Devices
//                                 </Button>
//                               )}

//                               </div>
//                               <Typography variant="h6">
//                                 <strong>Download</strong>
//                                 </Typography>
//                               <div style={{display:'flex', flexDirection:'row'}}>
//                               {product.windowsDownloadURI && (
//                                 <Button href={product.windowsDownloadURI} target="_blank" variant="contained" sx={{ mt: 1, pt:2, margin:'auto', backgroundColor:style.color1, transition: 'filter 0.3s ease',  '&:hover': { filter: 'brightness(1.2)' } }}>
//                                   <div style={{display:'flex', flexDirection:'column'}}>
//                                     <div style={{backgroundColor:'black', padding:'5px', paddingLeft:'10px', paddingRight:'10px', paddingTop:'10px'}}>
//                                       <img src='windows_logo.png' style={{maxWidth:'80px', maxHeight:'80px', margin:'auto'}} />
//                                     </div>
//                                     <div style={{textAlign:'center'}}>
//                                       <strong>Windows</strong>
//                                     </div>
//                                   </div>
              
//                                 </Button>
//                               )}
//                               {product.macDownloadURI && (
//                                 <Button href={product.macDownloadURI} target="_blank" variant="contained" sx={{ mt: 1, pt:2, margin:'auto', backgroundColor:style.color1, transition: 'filter 0.3s ease',  '&:hover': { filter: 'brightness(1.2)' } }}>
//                                 <div style={{display:'flex', flexDirection:'column'}}>
//                                   <div style={{backgroundColor:style.color4, padding:'5px', paddingLeft:'10px', paddingRight:'10px', paddingTop:'10px'}}>
//                                     <img src='apple_logo.svg' style={{maxWidth:'80px', maxHeight:'80px', margin:'auto'}} />
//                                   </div>
//                                   <div style={{textAlign:'center'}}>
//                                     <strong>Mac</strong>
//                                   </div>
//                                 </div>
            
//                               </Button>
//                               )}

//                               </div>
//                             </CardContent>
//                           </Card>
//                         </Grid>
//                       );
//                     })}
//                   </Grid>
//                 </Box>
//               )}
//             </>
//           )}

//           <Dialog open={isModalOpen} onClose={handleCloseDevicesModal} fullWidth maxWidth="md">
//             {!isMobile &&(
//               <>
//               <DialogTitle size="small" sx={{ display: 'flex', pt:0, pb:0,justifyContent: 'space-between', alignItems: 'center', backgroundColor: style.color2, color: style.color4 }}>
//                <span style={{fontSize:'1.2em', marginTop:'0px', marginBottom:'0px'}}>{selectedLicense?.productName}</span>
//               <IconButton onClick={handleCloseDevicesModal} sx={{ color: style.color4 }}>
//                 <CloseIcon />
//               </IconButton>
//             </DialogTitle>
            
//             <DialogContent dividers sx={{ backgroundColor: style.color1, color: style.color4 }}>
//               <div>

//               <h2 style={{fontSize:'1em', marginTop:'0px', marginBottom:'15px'}}>
//               Current Devices:
//               </h2>
//               <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, mt:0 }}>
//               <Typography sx={{fontSize:"1.2em", mr:1, ml:.5}}><strong>IP Address</strong></Typography>
//               <Typography sx={{fontSize:"1.2em", mr:1, ml:.5}}><strong>Device</strong></Typography>
//               <Typography sx={{fontSize:"1.2em", mr:1, ml:.5}}><strong>Remove </strong></Typography>
//                 </Box>
//               {selectedLicense?.machinesInstalledOn.map((device) => (
                
//                 <Box key={device.ipAddress} sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, mt:0, p:1 }}>
//                   <Typography sx={{fontSize:"1em", mr:.5, ml:.5}}>{device.ipAddress}</Typography>
//                   <Typography sx={{fontSize:"1em", mr:.5, ml:.5}}>{device.deviceName} ({device.osName})</Typography>
//                   <Button variant="outlined" sx={{ mt: 1, color:style.color4, borderColor:style.color3, minWidth:'75px', borderWidth:'1px', borderStyle:'solid',  backgroundColor:style.color1, transition: 'filter 0.3s ease',  '&:hover': { filter: 'brightness(1.2)' }}} size="small" onClick={() => handleRemoveDevice(device.ipAddress)}>
//                     Remove
//                   </Button>
//                 </Box>
//               ))}
//               </div>
//             </DialogContent>
//             <DialogActions sx={{ backgroundColor: style.color1 }}>

//               <Button sx={{ mt: 1, borderColor:style.color3, minWidth:'150px', borderWidth:'1px', borderStyle:'solid',  backgroundColor:style.color2, transition: 'filter 0.3s ease',  '&:hover': { filter: 'brightness(1.2)' }}} onClick={handleCloseDevicesModal} variant="contained">
//                 <strong>Close</strong>
//               </Button>
//             </DialogActions>

//               </>
//             )}




//               {isMobile &&(
//               <>
//               <DialogTitle size="small" sx={{ display: 'flex', pt:0, pb:0,justifyContent: 'space-between', alignItems: 'center', backgroundColor: style.color2, color: style.color4 }}>
//                <span style={{fontSize:'1.2em', marginTop:'0px', marginBottom:'0px'}}>{selectedLicense?.productName}</span>
//               <IconButton onClick={handleCloseDevicesModal} sx={{ color: style.color4 }}>
//                 <CloseIcon />
//               </IconButton>
//             </DialogTitle>
            
//             <DialogContent dividers sx={{ backgroundColor: style.color1, color: style.color4 }}>
//             <h2 style={{fontSize:'1em', marginTop:'0px', marginBottom:'15px'}}>
//               Current Devices:
//               </h2>
//               <Box sx={{display:'flex', flexDirection:'column'}}>
//               <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, mt:0 }}>
//               {/* <Typography sx={{fontSize:"1em", mr:1, ml:.5}}><strong>IP Address</strong></Typography>
//               <Typography sx={{fontSize:"1em", mr:1, ml:.5}}><strong>Device</strong></Typography>
//               <Typography sx={{fontSize:"1em", mr:1, ml:.5}}><strong>Remove </strong></Typography> */}
//                 </Box>
//               {selectedLicense?.machinesInstalledOn.map((device) => (
                
//                 <Box key={device.ipAddress} sx={{ display: 'flex', flexDirection:'column', justifyContent: 'space-between', mb: 2, mt:0, p:1, boxShadow:5, borderColor:style.color2, borderWidth:'1px', borderStyle:'solid' }}>
//                   <Typography sx={{fontSize:"1em", mr:.5, ml:.5}}>IP Address: {device.ipAddress}</Typography>
//                   <Typography sx={{fontSize:"1em", mr:.5, ml:.5}}>Device: {device.deviceName}</Typography>
//                   <Button variant="outlined" sx={{ mt: 1, color:style.color4, borderColor:style.color3, minWidth:'75px', borderWidth:'1px', borderStyle:'solid',  backgroundColor:style.color1, transition: 'filter 0.3s ease',  '&:hover': { filter: 'brightness(1.2)' }}} size="small" onClick={() => handleRemoveDevice(device.ipAddress)}>
//                     Remove
//                   </Button>
//                 </Box>
//               ))}
//               </Box>
//             </DialogContent>
//             <DialogActions sx={{ backgroundColor: style.color1 }}>

//               <Button sx={{ mt: 1, borderColor:style.color3, minWidth:'150px', borderWidth:'1px', borderStyle:'solid',  backgroundColor:style.color2, transition: 'filter 0.3s ease',  '&:hover': { filter: 'brightness(1.2)' }}} onClick={handleCloseDevicesModal} variant="contained">
//                 <strong>Close</strong>
//               </Button>
//             </DialogActions>

//               </>
//             )}

            
//           </Dialog>
//         </Box>
//       </div>
//     </div>
//     </>
//   );
// }



// import React, { useState, useEffect } from 'react';
// import {
//   Box,
//   Grid,
//   Card,
//   CardContent,
//   CardMedia,
//   Typography,
//   Button,
//   Select,
//   MenuItem,
//   FormControl,
//   InputLabel,
//   TextField
// } from '@mui/material';
// import { useStyle } from '../../context/styleContext/styleContext';
// import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
// import axios from 'axios';
// import { toast } from 'react-hot-toast';

// export default function MyGear() {
//   const { style } = useStyle();
//   const [gear, setGear] = useState([]);
//   const [licenses, setLicenses] = useState([]);
//   const [isLoading, setIsLoading] = useState(true);
//   const [filter, setFilter] = useState('All');
//   const [searchTerm, setSearchTerm] = useState('');
//   const [sortOption, setSortOption] = useState('nameAsc');
//   const [selectedLicense, setSelectedLicense] = useState(null);
//   const [isModalOpen, setIsModalOpen] = useState(false);

//   useEffect(() => {
//     document.title = 'Parish Audio | My Gear';
//     getMyGear();
//   }, []);

//   const getMyGear = async () => {
//     const token = localStorage.getItem('token');
//     try {
//       const { data } = await axios.post('/api/get_my_gear', { token });
//       if (data.error) {
//         toast.error(data.error);
//       } else {
//         setGear(data.products);
//         setLicenses(data.licenses);
//       }
//     } catch (error) {
//       console.log(error);
//     }
//     setIsLoading(false);
//   };

//   const getLicenseBySKU = (sku) => licenses.find((l) => l.sku === sku);

//   const handleOpenDevicesModal = (license) => {
//     setSelectedLicense(license);
//     setIsModalOpen(true);
//   };

//   const handleCloseDevicesModal = () => {
//     setSelectedLicense(null);
//     setIsModalOpen(false);
//   };

//   const handleRemoveDevice = async (deviceId) => {
//     const token = localStorage.getItem('token');
//     try {
//       const { data } = await axios.post('/api/remove_device_from_license', {
//         token,
//         licenseId: selectedLicense._id,
//         deviceId,
//       });
//       if (data.success) {
//         toast.success('Device removed');
//         getMyGear();
//         handleCloseDevicesModal();
//       } else {
//         toast.error(data.error || 'Failed to remove device');
//       }
//     } catch (err) {
//       toast.error('Error removing device');
//       console.error(err);
//     }
//   };

//   const filteredGear = gear
//     .filter((item) => {
//       if (filter === 'Mac' && !item.macDownloadURI) return false;
//       if (filter === 'Windows' && !item.windowsDownloadURI) return false;
//       return item.name.toLowerCase().includes(searchTerm.toLowerCase());
//     })
//     .sort((a, b) => {
//       if (sortOption === 'nameAsc') return a.name.localeCompare(b.name);
//       if (sortOption === 'nameDesc') return b.name.localeCompare(a.name);
//       return 0;
//     });

//   return (
//     <div className='image-background-1'>
//       <div className='dark-alpha-80'>
//         <Box sx={{ minHeight: '100vh', color: style.color4 }}>
//           {isLoading ? (
//             <Box sx={{ textAlign: 'center', mt: 10 }}><LoadingSpinner /></Box>
//           ) : (
//             <>
//               {gear.length === 0 ? (
//                 <Box textAlign="center" mt={10}>
//                   <Typography variant="h5">You do not yet have any equipment.</Typography>
//                   <Box mt={3}>
//                     <a href="/plugins" style={{ color: style.color3 }}>
//                       <Typography variant="h6">Shop All Plugins</Typography>
//                     </a>
//                   </Box>
//                 </Box>
//               ) : (
//                 <Box sx={{ maxWidth: 1200, mx: 'auto', p: 2 }}>
//                   <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
//                     <FormControl sx={{ minWidth: 180 }}>
//                       <InputLabel sx={{ color: style.color4 }}>Sort</InputLabel>
//                       <Select
//                         value={sortOption}
//                         onChange={(e) => setSortOption(e.target.value)}
//                         size="small"
//                         sx={{ color: style.color4, backgroundColor: style.color1 }}
//                       >
//                         <MenuItem value="nameAsc">Name A–Z</MenuItem>
//                         <MenuItem value="nameDesc">Name Z–A</MenuItem>
//                       </Select>
//                     </FormControl>

//                     <TextField
//                       variant="outlined"
//                       placeholder="Search by name..."
//                       value={searchTerm}
//                       size="small"
//                       onChange={(e) => setSearchTerm(e.target.value)}
//                       sx={{ minWidth: 250, backgroundColor: style.color1, input: { color: style.color4 } }}
//                     />
//                   </Box>

//                   <Grid container spacing={3}>
//                     {filteredGear.map((product) => {
//                       const license = getLicenseBySKU(product.SKU);
//                       const machines = license?.machinesInstalledOn || [];

//                       return (
//                         <Grid item xs={12} sm={6} md={4} key={product.SKU}>
//                           <Card sx={{ backgroundColor: style.color1, color: style.color4 }}>
//                             <a href={`/products/${product.productURI}`}>
//                               <CardMedia
//                                 component="img"
//                                 image={product.images?.[0]?.URI || '/default-image.png'}
//                                 alt={product.images?.[0]?.altText || 'Product image'}
//                                 sx={{ maxHeight: 200, objectFit: 'contain', p: 1 }}
//                               />
//                             </a>
//                             <CardContent>
//                               <Typography variant="h6">
//                                 <a href={`/products/${product.productURI}`} style={{ color: style.color4 }}>{product.name}</a>
//                               </Typography>
//                               <Typography variant="body2">{product.shortDescription}</Typography>
//                               <Typography variant="body2" sx={{ mt: 1 }}>
//                                 Installed on {machines.length} device{machines.length !== 1 ? 's' : ''}
//                               </Typography>
//                               {machines.length > 0 && (
//                                 <Button variant="outlined" onClick={() => handleOpenDevicesModal(license)} sx={{ mt: 1 }}>
//                                   View Devices
//                                 </Button>
//                               )}
//                               {product.windowsDownloadURI && (
//                                 <Button href={product.windowsDownloadURI} target="_blank" variant="contained" sx={{ mt: 1 }}>
//                                   Download for Windows
//                                 </Button>
//                               )}
//                               {product.macDownloadURI && (
//                                 <Button href={product.macDownloadURI} target="_blank" variant="contained" sx={{ mt: 1 }}>
//                                   Download for Mac
//                                 </Button>
//                               )}
//                             </CardContent>
//                           </Card>
//                         </Grid>
//                       );
//                     })}
//                   </Grid>
//                 </Box>
//               )}
//             </>
//           )}

//           {selectedLicense && (
//             <Box
//               sx={{
//                 position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
//                 bgcolor: 'rgba(0,0,0,0.8)', display: isModalOpen ? 'flex' : 'none',
//                 alignItems: 'center', justifyContent: 'center', zIndex: 1300
//               }}
//               onClick={handleCloseDevicesModal}
//             >
//               <Box
//                 onClick={(e) => e.stopPropagation()}
//                 sx={{ backgroundColor: style.color1, p: 3, borderRadius: 2, width: 500, color: style.color4 }}
//               >
//                 <Typography variant="h6" mb={2}>Devices for SKU {selectedLicense.sku}</Typography>
//                 {selectedLicense.machinesInstalledOn.map((device) => (
//                   <Box key={device._id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
//                     <Typography>{device.deviceName} ({device.osName})</Typography>
//                     <Button onClick={() => handleRemoveDevice(device._id)} size="small" variant="outlined">
//                       Remove
//                     </Button>
//                   </Box>
//                 ))}
//                 <Button onClick={handleCloseDevicesModal} sx={{ mt: 2 }} variant="contained">Close</Button>
//               </Box>
//             </Box>
//           )}
//         </Box>
//       </div>
//     </div>
//   );
// }



// import React, { useState, useEffect } from 'react';
// import {
//   Box,
//   Grid,
//   Card,
//   CardContent,
//   CardMedia,
//   Typography,
//   Button,
//   Select,
//   MenuItem,
//   FormControl,
//   InputLabel,
//   TextField
// } from '@mui/material';
// import { useStyle } from '../../context/styleContext/styleContext';
// import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
// import axios from 'axios';
// import { toast } from 'react-hot-toast';

// export default function MyGear() {
//   const { style } = useStyle();
//   const [gear, setGear] = useState([]);
//   const [licenses, setLicenses] = useState([]);
//   const [isLoading, setIsLoading] = useState(true);
//   const [filter, setFilter] = useState('All');
//   const [searchTerm, setSearchTerm] = useState('');
//   const [sortOption, setSortOption] = useState('nameAsc');

//   useEffect(() => {
//     document.title = 'Parish Audio | My Gear';
//     getMyGear();
//   }, []);

//   const getMyGear = async () => {
//     const token = localStorage.getItem('token');
//     try {
//       const { data } = await axios.post('/api/get_my_gear', { token });
//       if (data.error) {
//         toast.error(data.error);
//       } else {
//         setGear(data.products);
//         setLicenses(data.licenses)
//         console.log(data);
//       }
//     } catch (error) {
//       console.log(error);
//     }
//     setIsLoading(false);
//   };

//   const filteredGear = gear
//     .filter((item) => {
//       if (filter === 'Mac' && !item.macDownloadURI) return false;
//       if (filter === 'Windows' && !item.windowsDownloadURI) return false;
//       if (!item.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
//       return true;
//     })
//     .sort((a, b) => {
//       if (sortOption === 'nameAsc') return a.name.localeCompare(b.name);
//       if (sortOption === 'nameDesc') return b.name.localeCompare(a.name);
//       return 0;
//     });

//   return (
//     <div className='image-background-1'>
//           <div className='dark-alpha-80'>
//     <Box sx={{ minHeight: '100vh', color: style.color4, py: 0 }}>
      

//       {isLoading ? (
//         <Box sx={{ textAlign: 'center', mt: 10 }}>
//           <LoadingSpinner />
//         </Box>
//       ) : (
//         <>
//           {gear.length === 0 ? (
//             <Box textAlign="center" mt={10}>
//               <Typography variant="h5">You do not yet have any equipment.</Typography>
//               <Box mt={3}>
//                 <a href="/plugins" style={{ color: style.color3 }}>
//                   <Typography variant="h6">Shop All Plugins</Typography>
//                 </a>
//               </Box>
//             </Box>
//           ) : (
//             <div>
//                 <div style={{maxWidth: 1200, margin:'auto', marginTop:'10px', marginBottom:'10px'}}>
//                     <h1 style={{marginTop:'0px'}}>My Gear</h1>
//                 </div>
//             <Box sx={{ maxWidth: 1200, mx: 'auto', px: 2, backgroundColor:'black', borderColor:style.color1, borderWidth:'2px', borderStyle:'solid', py:2 }}>
//               {/* Centered Filters and Search */}
//               <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>

//               <FormControl sx={{ minWidth: 180 }}>
//                 <InputLabel
//                     sx={{
//                     color: style.color4,
//                     '&.Mui-focused': {
//                         color: style.color3,
//                     },
//                     }}
//                 >
//                     Sort
//                 </InputLabel>
//                 <Select
//                     value={sortOption}
//                     onChange={(e) => setSortOption(e.target.value)}
//                     size="small"
//                     sx={{
//                     color: style.color4,
//                     backgroundColor: style.color1,
//                     borderRadius: 1,
//                     '.MuiSvgIcon-root': { color: style.color4 },
//                     '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
//                         borderColor: style.color3,
//                     },
//                     '& .MuiOutlinedInput-notchedOutline': {
//                         borderColor: 'transparent',
//                     },
//                     '&:hover .MuiOutlinedInput-notchedOutline': {
//                         borderColor: style.color3,
//                     },
//                     '&.Mui-focused fieldset': {
//                         borderColor: style.color3,
//                     },
//                     }}
//                 >
//                     <MenuItem value="nameAsc">Name A–Z</MenuItem>
//                     <MenuItem value="nameDesc">Name Z–A</MenuItem>
//                 </Select>
//                 </FormControl>

//                 <TextField
//                 variant="outlined"
//                 placeholder="Search by name..."
//                 value={searchTerm}
//                 size="small"
//                 onChange={(e) => setSearchTerm(e.target.value)}
//                 sx={{
//                     minWidth: 250,
//                     backgroundColor: style.color1,
//                     input: { color: style.color4 },
//                     '& .MuiOutlinedInput-root': {
//                     '& fieldset': {
//                         borderColor: 'transparent',
//                     },
//                     '&:hover fieldset': {
//                         borderColor: style.color3,
//                     },
//                     '&.Mui-focused fieldset': {
//                         borderColor: style.color3,
//                     },
//                     },
//                     '& .MuiInputLabel-root': {
//                     color: style.color4,
//                     '&.Mui-focused': {
//                         color: style.color3,
//                     },
//                     },
//                 }}
//                 />
//               </Box>

//               {/* Gear Grid */}
//               <Box display="flex" justifyContent="center">
//                 <Grid container spacing={3} justifyContent="center" maxWidth="lg">
//                   {filteredGear.map((product) => (
//                     <Grid item xs={12} sm={6} md={4} key={product.SKU}>
//                       <Card
//                         sx={{
//                           height: '100%',
//                           display: 'flex',
//                           flexDirection: 'column',
//                           backgroundColor: style.color1,
//                           color: style.color4,
//                         }}
//                       >
//                         <a href={'/products/' + product.productURI}>
//                             <CardMedia
//                             component="img"
//                             image={product.images?.[0]?.URI || '/default-image.png'}
//                             alt={product.images?.[0]?.altText || 'Product image'}
//                             sx={{
//                                 maxHeight: 200,
//                                 objectFit: 'contain',
//                                 backgroundColor: 'rgba(0, 0, 0, 0.6)',
//                                 border: `5px solid ${style.color1}`,
//                                 borderRadius: 1,
//                                 p: 1,
//                                 boxSizing: 'border-box',
//                             }}
//                             /></a>
//                         <CardContent sx={{ flexGrow: 1 }}>
//                           <Typography variant="h6" gutterBottom>
//                           <a href={'/products/' + product.productURI}>{product.name}</a>
//                           </Typography>
//                           <Typography variant="body2" sx={{ mb: 2 }}>
//                             {product.shortDescription}
//                           </Typography>
//                           <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
//                             {product.windowsDownloadURI && (
//                               <Button
//                                 variant="contained"
//                                 href={product.windowsDownloadURI}
//                                 target="_blank"
//                                 sx={{
//                                   backgroundColor: style.color2,
//                                   color: style.color4,
//                                   '&:hover': {
//                                     backgroundColor: style.color3,
//                                   },
//                                 }}
//                               >
//                                     Download for Windows

//                               </Button>
//                             )}
//                             {product.macDownloadURI && (
//                               <Button
//                                 variant="contained"
//                                 href={product.macDownloadURI}
//                                 target="_blank"
//                                 sx={{
//                                   backgroundColor: style.color2,
//                                   color: style.color4,
//                                   '&:hover': {
//                                     backgroundColor: style.color3,
//                                   },
//                                 }}
//                               >
//                                 Download for Mac
//                               </Button>
//                             )}
//                           </Box>
//                         </CardContent>
//                       </Card>
//                     </Grid>
//                   ))}
//                 </Grid>
//               </Box>
//             </Box>
//             </div>
//           )}
//         </>
//       )}
//     </Box>
//     </div>
//     </div>
//   );
// }